import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { storiesApi } from '../api/stories';
import { sceneWritingApi } from '../api/sceneWriting';
import { peopleForPov, styleCardsApi } from '../api/styleCards';
import { modelsApi } from '../api/models';
import { styleCardsPath } from '../utils/styleCardsPath';
import { modelMenuNames, splitStyleSample, styleRoomEnabled, styleSourceLabel, writtenWithLine } from '../utils/styleRoom';
import { castIdsFromBookPov } from '../utils/narrativePov';
import { personModeLabel, sectionPersonBody } from '../utils/personMode';
import { plottrackApi } from '../api/plottrack';
import { chapterCoveringScene } from '../utils/outlineCardRows';
import PlotTrackAddScene from '../components/PlotTrack/PlotTrackAddScene';
import { entitiesApi } from '../api/entities';
import { useSyncStoryContext } from '../hooks/useSyncStoryContext';
import { useBreakpoint } from '../hooks/useBreakpoint';
import StoryPicker from '../components/Common/StoryPicker';
import { publishChatSection, publishChatSubject, writeRoomChatSubject } from '../components/Chat/ChatPaneFrame';
import JourneyNextTip from '../components/Common/JourneyNextTip';
import BookToolsGroup from '../components/BookTools/BookToolsGroup';
import PersonControl from '../components/Common/PersonControl';
import WhoseHeadControl from '../components/Common/WhoseHeadControl';
import WriteDraftConfirm from '../components/SceneWriting/WriteDraftConfirm';
import StyleChoice from '../components/SceneWriting/StyleChoice';
import WriteMoreMenu from '../components/SceneWriting/WriteMoreMenu';
import NewDraftOnAcceptedDialog from '../components/SceneWriting/NewDraftOnAcceptedDialog';
import RestoreAcceptedDialog from '../components/SceneWriting/RestoreAcceptedDialog';
import ShrinkGuardDialog from '../components/SceneWriting/ShrinkGuardDialog';
import AcceptNoteCheckCost from '../components/SceneWriting/AcceptNoteCheckCost';
import PaidActionConfirm from '../components/Common/PaidActionConfirm';
import { betaAiPaused } from '../utils/betaSpendPause';
import { isGenericCheckSource, parseDraftCheck } from '../utils/draftCheck';
import {
  NEW_PASS_ON_THE_WAY,
  SCENE_WRITE_POLL_MS,
  clearSceneWrite,
  followSceneWrite,
  presentSceneWrite,
  readLastScene,
  readSceneWrite,
  rememberLastScene,
  rememberSceneWrite,
  sceneGenerationView,
} from '../utils/sceneWriteRecovery';
import { useSceneDraftAutosave } from '../hooks/useSceneDraftAutosave';
import { readUnsavedDraft, removeUnsavedDraft } from '../utils/sceneDraftLocal';
import {
  SCENE_VOICE_POINTED,
  readSceneVoicePoint,
  sceneVoicePointKey,
  sceneVoicePointedAfter,
} from '../utils/sceneVoicePointed';
import { publishChatFocus } from '../components/Chat/ChatPaneFrame';
import DetachedDrafts from '../components/SceneWriting/DetachedDrafts';
import CastOffer from '../components/Notes/CastOffer';
import BringSeriesPeopleOffer from '../components/Notes/BringSeriesPeopleOffer';
import { usePaidConfirm } from '../components/Common/usePaidConfirm';
import { journeyNextTip } from '../utils/journeyNextTip';
import {
  isRawSceneError,
  sceneAddress,
  sceneWritingHandoffFromSearch,
  sceneWritingPath,
} from '../utils/sceneWritingPath';
import {
  PROSE_SCENE_GENERATION_DEFAULT_MODEL,
} from '../utils/estimateSceneWritingCost';
import {
  WHOSE_HEAD_CHOICE_ALL,
  WHOSE_HEAD_CHOICE_NOT_SET,
  WHOSE_HEAD_CHOICE_PERSON,
} from '../utils/plottrackGrid';
import {
  RESUME_PAST_ACCEPTED,
  SCENE_IN_SEQUENCE,
  ACCEPT_ALREADY,
  ACCEPT_NEED_DRAFT,
  ACCEPT_NOTE_UPDATE,
  ACCEPT_DRAFT,
  REWRITE_NOTE_LABEL,
  REWRITE_NOTE_PLACEHOLDER,
  REWRITE_DRAFT,
  UNDO_ACCEPT,
  UNDO_DISMISS,
  AS_OF_ENTRY_HINT,
  CANCEL_EDIT_NOTE_UPDATE,
  CHOOSE_A_VOICE,
  CONTINUE_THIS_DRAFT,
  CONTINUE_VS_NEW_DRAFT,
  CURRENT_STATE_AFTER_SCENE,
  EDIT_NOTE_UPDATE,
  ENTRY_HEADING,
  EXIT_HEADING,
  INSTRUCTIONS_OPTIONAL,
  NOTES_FOR_THIS_DRAFT,
  DRAFT_NOTES_PLACEHOLDER,
  CHECK_BEFORE_ACCEPTING,
  CHECK_BEFORE_ACCEPTING_HINT,
  CHECK_COULD_NOT_RUN,
  CHECK_READ_IT_YOURSELF,
  CHECK_NO_DISAGREEMENTS,
  CHECK_THAT_NOTE,
  CHECK_THAT_SCENE,
  CHECK_THIS_DRAFT,
  KNOWLEDGE_WALLS_HEADING,
  MUST_STAY_SECRET_EMPTY,
  MUST_STAY_SECRET_HEADING,
  MUST_STAY_SECRET_HINT,
  MUTABLE_NOTE_CHIP,
  ADD_TO_THE_NOTE,
  EDIT_THIS_FACT,
  LINE_BECOMES,
  NOTE_OFFERS_LEDE,
  ONE_NOTE_CHANGE,
  PLOT_TRACK,
  PRIOR_PROSE_HEADING,
  SEQUENCE_BOUNDARY_LINE,
  STAYS_ON_THE_NOTE,
  THIS_LINE_CHANGES,
  PASS_ACCEPTED,
  PERSON_BOOK_DEFAULT_SET,
  PERSON_SAVE_FAILED,
  PASS_IS_DRAFT,
  PASS_STOPPED_EARLY,
  ACCEPTED_SCENE_EDIT_HINT,
  BRING_BACK_EDITED_DRAFT,
  DRAFT_LEAVE_WARNING,
  EDIT_ACCEPTED_SCENE,
  STOP_EDITING,
  EDITED_SINCE_NOTES,
  DRAFT_SAVED,
  DRAFT_SAVE_FAILED,
  DRAFT_SAVE_RETRY,
  DRAFT_SAVING,
  PICK_ANOTHER_VOICE,
  PROPOSED_NOTE_UPDATES,
  HIDE_SCENE_CONTEXT,
  REVIEW_CONTEXT,
  RESTORE_ACCEPTED_VERSION,
  REVISE_THIS_PASS,
  REVISE_THIS_PASS_HINT,
  SCENE_DRAFT_LABEL,
  COPY_SCENE_DRAFT,
  COPIED_SCENE_DRAFT,
  SHOW_SCENE_CONTEXT,
  ROW_ORDER_NOT_BEAT_ORDER,
  RULE_SITS_NEXT_TO_VOICE,
  SAVE_EDITED_NOTE_UPDATE,
  SCENE_CONTEXT_HEADING,
  SCENE_CONTEXT_LEDE,
  SCENE_VOICE_REPLACES,
  SCENE_WRITE_PLOTTRACK_HINT,
  SKIP_NOTE_UPDATE,
  STANDING_IDENTITY_STAYS,
  USE_THIS_VOICE,
  COPY_BOOK_VOICE_INTO_SCENE,
  MAKE_THIS_THE_BOOK_VOICE,
  RULE_ABOUT_VOICE,
  KEEP_THE_VOICE,
  VOICE_RULE_YIELDS,
  isPlaceholderStyleName,
  NO_VOICE_ON_SCENE,
  NO_STYLE_SET,
  NO_VOICE_YET,
  SAMPLE_OF_THE_SOUND,
  VOICE_LABEL,
  WHOSE_HEAD_ALL,
  WHOSE_HEAD_USE_PREVIOUS,
  WRITE_DRAFT,
  SCENE_SETTINGS,
  WRITE_NEED_FIGHT,
  SCENE_WRITE_NO_SCENES,
  WRITE_NEED_OUTLINE,
  WRITE_NEED_SCENE,
} from '../utils/writerCopy';
import useBookPath, { noteBookPathChanged } from '../hooks/useBookPath';
import { bookPathTip } from '../utils/bookPathTip';
import { CHAT_TEXT_SIZE, readChatZoom } from '../utils/textSizeRange';
import { isPlaceholderSceneName } from '../utils/plottrackGrid';
import './SceneWritingPage.css';

const GENERATE_LOST_COPY = (
  'The scene did not come through. Your instruction is still here.'
);

// Spur Creek Two Step: while the outline list is still opening, the
// picker stays aimed at the first scene. Never flash empty Choose.
const OPENING_FIRST_SCENE = 'opening-first-scene';
const OPENING_FIRST_SCENE_LABEL = 'Opening the first outline scene…';
const STILL_OPENING_SCENE = 'Still opening this scene.';
const STILL_OPENING_FIRST = 'Still opening the first outline scene.';
const TRY_AGAIN_LABEL = 'Try again';
export const OPENING_SLOW_MS = 8000;
/** Pause after a note edit before asking for the draft cost again. */
const ESTIMATE_SETTLE_MS = 400;

/**
 * What actually changes the draft cost. A new compose object that only
 * carries a progress tick is the same key, so it does not ask again.
 */
export function draftEstimateSignature({
  storyId,
  sceneId,
  instruction,
  resolutions,
  compose,
} = {}) {
  const scene = sceneId || compose?.scene_id || '';
  if (!storyId || !scene) return '';
  const book = compose?.scene_context?.book_notes || {};
  const noteGroups = ['standing', 'mutable_note_wins', 'mutable_latest', 'mutable_as_of_entry'];
  const notes = noteGroups.map((key) => (
    (book[key] || []).map((note) => `${note?.name || ''}\n${note?.text || ''}`).join('\n')
  )).join('\n');
  const earlier = (compose?.scene_context?.earlier_accepted || [])
    .map((row) => `${row?.label || row?.name || ''}\n${row?.text || ''}`)
    .join('\n');
  const rules = (compose?.applying_rules || [])
    .map((rule) => `${rule?.id || ''}:${rule?.enabled === false ? '0' : '1'}:${rule?.sentence || rule?.text || ''}`)
    .join('\n');
  const parts = [
    storyId,
    String(scene),
    String(instruction || '').trim(),
    JSON.stringify(resolutions || []),
    compose?.card?.resolved_text || '',
    compose?.pov?.mode || compose?.pov?.mode_label || '',
    compose?.whose_head?.choice || '',
    notes,
    earlier,
    rules,
  ];
  if (styleRoomEnabled()) parts.push(compose?.card?.exemplars || '');
  return parts.join('\u0001');
}

const CHARGE_VISIBLE_EVENT = 'openrouter:balance-changed';

/** One click to reuse the nearest earlier scene's POV character. Never sets it on its own. */
export function PreviousPovOffer({ previous, current, disabled, onUse }) {
  if (!previous || !previous.choice) return null;
  if (current && current.choice && current.choice !== WHOSE_HEAD_CHOICE_NOT_SET) return null;
  const isAll = previous.choice === WHOSE_HEAD_CHOICE_ALL;
  const name = isAll ? WHOSE_HEAD_ALL : (previous.entity_name || '').trim();
  if (!name) return null;
  return (
    <button
      type="button"
      className="sw-btn sw-pov-offer"
      data-testid="sw-previous-pov"
      disabled={disabled}
      onClick={() => onUse({
        choice: previous.choice,
        entityId: isAll ? '' : (previous.entity_id || ''),
        entityName: isAll ? '' : name,
      })}
    >
      {WHOSE_HEAD_USE_PREVIOUS(name, previous.scene_name)}
    </button>
  );
}

function personHead(entityId, entityName) {
  return {
    choice: WHOSE_HEAD_CHOICE_PERSON,
    entityId: entityId || '',
    entityName: entityName,
  };
}

/**
 * Show the book's lead when this scene has not named one.
 * Lead is the book's POV person, else its single POV cast member,
 * else the POV character an earlier scene already uses.
 * Display only — picking Not set still saves Not set.
 */
export function povChipValue(whoseHead, bookPov, previous) {
  const head = whoseHead || {
    choice: WHOSE_HEAD_CHOICE_NOT_SET,
    entityId: '',
    entityName: '',
  };
  const bookName = (bookPov?.entity_name || '').trim();
  const bookId = bookPov?.entity_id || '';
  if (head.choice === WHOSE_HEAD_CHOICE_PERSON) {
    if ((head.entityName || '').trim()) return head;
    if (bookName && (!head.entityId || !bookId || String(head.entityId) === String(bookId))) {
      return { ...head, entityId: head.entityId || bookId, entityName: bookName };
    }
    return head;
  }
  if (head.choice === WHOSE_HEAD_CHOICE_ALL) return head;
  if (head.choice && head.choice !== WHOSE_HEAD_CHOICE_NOT_SET) return head;
  if (bookName) return personHead(bookId, bookName);
  const cast = (bookPov?.cast || []).filter((person) => (person?.name || '').trim());
  if (cast.length === 1) return personHead(cast[0].entity_id, cast[0].name.trim());
  const earlierName = (previous?.entity_name || previous?.entityName || '').trim();
  if (previous?.choice === WHOSE_HEAD_CHOICE_PERSON && earlierName) {
    return personHead(previous.entity_id || previous.entityId || '', earlierName);
  }
  if (previous?.choice === WHOSE_HEAD_CHOICE_ALL) {
    return { choice: WHOSE_HEAD_CHOICE_ALL, entityId: '', entityName: '' };
  }
  return head;
}

export function peopleWithLead(people, head) {
  if (!head || head.choice !== WHOSE_HEAD_CHOICE_PERSON) return people || [];
  const name = (head.entityName || '').trim();
  if (!head.entityId || !name) return people || [];
  if ((people || []).some((person) => String(person.id) === String(head.entityId))) {
    return people || [];
  }
  return [{ id: head.entityId, name }, ...(people || [])];
}

function headFromCompose(compose, handoff) {
  const raw = compose?.whose_head;
  if (raw && raw.choice) {
    return {
      choice: raw.choice,
      entityId: raw.entity_id || '',
      entityName: raw.entity_name || '',
    };
  }
  if (!compose && handoff?.viewpoint === 'All') {
    return { choice: WHOSE_HEAD_CHOICE_ALL, entityId: '', entityName: '' };
  }
  if (!compose && handoff?.viewpoint) {
    return {
      choice: WHOSE_HEAD_CHOICE_PERSON,
      entityId: '',
      entityName: handoff.viewpoint,
    };
  }
  return { choice: WHOSE_HEAD_CHOICE_NOT_SET, entityId: '', entityName: '' };
}

function peopleFromCompose(compose) {
  return peopleForPov({
    people_for_pov: compose?.people_for_pov,
    characters: compose?.characters,
    character_cards: compose?.character_cards,
  }).map((person) => ({
    id: person.pickId || person.character_entity_id || person.bible_entry_id,
    name: person.name,
  })).filter((person) => person.id && person.name);
}

const instructionStoreKey = (storyId, sceneId) => (
  `brainstormy.scene-writing.instruction.${storyId}.${sceneId}`
);

const CONTEXT_OPEN_KEY = 'brainstormy.scene-writing.context-open';

/** No stored choice is a first visit: open, so Write draft is on screen.
 *  '0' is a later collapse in this tab and stays closed. '1' stays open. */
const readContextOpen = () => {
  if (typeof sessionStorage === 'undefined') return true;
  try {
    const stored = sessionStorage.getItem(CONTEXT_OPEN_KEY);
    if (stored == null) return true;
    return stored === '1';
  } catch {
    return true;
  }
};

const headWarnKey = (storyId) => `brainstormy.scene-writing.head-warned.${storyId}`;

/** #1340: true once the writer chose "Write anyway" for this book this visit. */
export function headWarnSeen(storyId) {
  try {
    return window.sessionStorage.getItem(headWarnKey(storyId)) === '1';
  } catch (err) {
    return false;
  }
}

export function markHeadWarnSeen(storyId) {
  try {
    window.sessionStorage.setItem(headWarnKey(storyId), '1');
  } catch (err) {
    // Private mode: the warning may show again. Never block the draft.
  }
}

/** #1340: "2.1 The Washout" — the number follows the outline (sequence.scene).
 * "Scene 1" is the same placeholder the Outline cards show as Untitled scene.
 * "Scene 2.2" is a real title and stays.
 */
export function sceneOptionLabel(scene, fallback) {
  const stored = String(scene?.display_name || '').trim();
  const raw = stored || String(fallback || '').trim();
  const name = (!raw || isPlaceholderSceneName(raw)) ? 'Untitled scene' : raw;
  return scene?.number ? `${scene.number} ${name}` : name;
}

/**
 * The scene's chapter from the outline map: the name on the scene row,
 * else the chapter whose scene_ids cover it, else the compose field.
 * Empty when the scene is not in a chapter. Same "Chapter N" the grid uses.
 */
export function chapterBesideScene(scenes, sceneId, chapterName, chapters) {
  const scene = (scenes || []).find((row) => String(row?.id) === String(sceneId));
  const fromScene = String(scene?.chapter_name || '').trim();
  if (fromScene) return fromScene;
  const fromMap = String(chapterCoveringScene(chapters, sceneId)?.display_name || '').trim();
  if (fromMap) return fromMap;
  return String(chapterName || '').trim();
}

/** The open scene, by title and sequence, with its chapter when it has one. */
export function sceneHeaderLine(scenes, sceneId, chapterName, chapters) {
  if (!sceneId) return '';
  const scene = (scenes || []).find((row) => String(row?.id) === String(sceneId));
  if (!scene) return '';
  const line = SCENE_IN_SEQUENCE(sceneOptionLabel(scene), String(scene.sequence_name || '').trim());
  const chapter = chapterBesideScene(scenes, sceneId, chapterName, chapters);
  return chapter ? `${line} · ${chapter}` : line;
}

/** Consecutive scenes of one sequence share an option group, in outline order. */
export function sceneGroups(scenes) {
  const groups = [];
  (scenes || []).forEach((scene, index) => {
    const name = scene?.sequence_name || '';
    const last = groups[groups.length - 1];
    if (last && last.name === name) {
      last.scenes.push(scene);
    } else {
      groups.push({ key: `${name || 'scenes'}-${index}`, name, scenes: [scene] });
    }
  });
  return groups;
}

/**
 * The scene's chapter from compose. The name on the scene row, else the
 * chapter whose scene_ids cover it. Empty when the scene is not in a
 * chapter. Does not use the open scene's chapter for every row, and does
 * not name the run before the first break (#1916).
 */
export function chapterNameForScene(scene, chapters) {
  const fromScene = String(scene?.chapter_name || '').trim();
  if (fromScene) return fromScene;
  return String(chapterCoveringScene(chapters, scene?.id)?.display_name || '').trim();
}

/** True when compose has placed at least one scene in a chapter. */
export function bookHasChapterBreaks(scenes, chapters) {
  if ((chapters || []).some((chapter) => (chapter?.scene_ids || []).length > 0)) return true;
  return (scenes || []).some((scene) => String(scene?.chapter_name || '').trim());
}

function sceneGroupLabel(chapter, sequence) {
  if (chapter && sequence) return `${chapter} · ${sequence}`;
  return chapter || sequence || '';
}

/**
 * Scene menu groups (#2014).
 *
 * No chapter breaks: the same sequence groups as sceneGroups.
 *
 * With breaks: those sequence runs sit under the chapter compose named.
 * A native menu has one group level, so the group label is the chapter
 * and then the sequence. A chapter that holds two sequences is two
 * groups that share that chapter name. Scenes before the first break
 * keep the sequence label alone.
 */
export function scenePickerGroups(scenes, chapters) {
  if (!bookHasChapterBreaks(scenes, chapters)) return sceneGroups(scenes);
  const groups = [];
  (scenes || []).forEach((scene, index) => {
    const sequence = scene?.sequence_name || '';
    const chapter = chapterNameForScene(scene, chapters);
    const last = groups[groups.length - 1];
    if (last && last.chapter === chapter && last.sequence === sequence) {
      last.scenes.push(scene);
      return;
    }
    groups.push({
      key: `${chapter || 'open'}-${sequence || 'scenes'}-${index}`,
      name: sceneGroupLabel(chapter, sequence),
      chapter,
      sequence,
      scenes: [scene],
    });
  });
  return groups;
}

export const readStoredInstruction = (storyId, sceneId) => {
  if (!storyId || !sceneId || typeof sessionStorage === 'undefined') return '';
  try {
    return sessionStorage.getItem(instructionStoreKey(storyId, sceneId)) || '';
  } catch {
    return '';
  }
};

/** Writer notes only. An empty box removes the key so a clear stays cleared. */
export const writeStoredInstruction = (storyId, sceneId, text) => {
  if (!storyId || !sceneId || typeof sessionStorage === 'undefined') return;
  try {
    const key = instructionStoreKey(storyId, sceneId);
    if (text && text.trim()) sessionStorage.setItem(key, text);
    else sessionStorage.removeItem(key);
  } catch {
    /* ignore quota / private mode */
  }
};

/**
 * The Style Notes card Write sends (#2191). Shown first under
 * "What Write will send". The notes box does not replace it.
 */
export function voiceTextFromCompose(compose) {
  return String(compose?.card?.resolved_text || '').trim();
}

/** A saved placeholder is not a name. Book default is the existing book-voice wording. */
export function voiceNameFromCompose(compose) {
  const usable = (value) => {
    const text = String(value || '').trim();
    if (isPlaceholderStyleName(text)) return '';
    return text;
  };
  const own = usable(compose?.card?.name);
  if (own) return own;
  const parent = usable(compose?.started_from_card?.name);
  if (parent) return parent;
  if (compose?.card || compose?.card_source) return 'Book default';
  return '';
}

/**
 * The POV line the server adds (#1467). Shown under "What Write will send",
 * never typed into the writer's box. Matches scene_writing._pov_line.
 */
export function povLineFromCompose(compose) {
  const head = compose?.whose_head || {};
  const pov = compose?.pov || {};
  const choice = String(head.choice || '').trim();
  if (choice === 'all') return 'POV character: All — no single POV character.';
  let name = null;
  if (choice === 'person') name = head.entity_name || pov.entity_name || null;
  else if (choice === 'not_set') name = null;
  else name = pov.entity_name || null;
  const bits = [];
  if (pov.mode_label) bits.push(pov.mode_label);
  if (name) bits.push(name);
  if (!bits.length) return '';
  const source = (choice === 'person' ? head.source : null) || pov.source;
  const sourceBit = source ? ` (from ${source})` : '';
  return `POV: ${bits.join(', ')}${sourceBit}.`;
}

/** Hard rules Write will add. An unresolved fight is not sent yet. */
export function hardRuleLines(compose) {
  const unresolved = new Set(
    (compose?.unresolved_fights || []).map((fight) => String(fight?.rule_id || '')),
  );
  return (compose?.applying_rules || [])
    .filter((rule) => (
      rule
      && rule.enabled !== false
      && String(rule.sentence || '').trim()
      && !unresolved.has(String(rule.id || ''))
    ))
    .map((rule) => String(rule.sentence).trim());
}

const firstSceneId = (payload) => (
  payload?.scene_id || payload?.scenes?.[0]?.id || ''
);

const passProseRaw = (payload) => {
  const nested = payload?.last_pass?.generated_text;
  if (typeof nested === 'string') return nested;
  const top = payload?.generated_text;
  if (typeof top === 'string') return top;
  return '';
};

const passProse = (payload) => passProseRaw(payload).trim();

const passKey = (payload) => (
  payload?.last_pass?.id || payload?.pass_id || passProse(payload) || ''
);

const markChargeWhenProseVisible = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(CHARGE_VISIBLE_EVENT));
};

const lostGenerateMessage = (err) => {
  const msg = (err?.message || '').toLowerCase();
  if (msg.includes('did not come through') || msg.includes('generated_text')) {
    return GENERATE_LOST_COPY;
  }
  return err?.message || 'That scene-writing action failed.';
};

const unwrapList = (res) => (Array.isArray(res) ? res : res?.data || []);

const byRecency = (a, b) => {
  const at = new Date(a.last_activity_at || a.created_at).getTime();
  const bt = new Date(b.last_activity_at || b.created_at).getTime();
  return bt - at;
};

const sourceLabel = (source) => {
  if (source === 'scene') return 'this scene';
  if (source === 'chapter') return 'this chapter';
  if (source === 'book') return 'this book';
  return source || 'unset';
};

// A plain focus() puts the caret at the end and scrolls the tail into view,
// so only the last paragraph looks editable. Open on the start of the pass.
const focusWholeDraft = (el) => {
  if (!el) return;
  el.focus({ preventScroll: true });
  if (typeof el.setSelectionRange === 'function') {
    el.setSelectionRange(0, 0);
  }
  el.scrollTop = 0;
};

/**
 * Scene writing room (#857 / #1030). Cards and rules sit in the same room,
 * set apart. At generate they become the one instruction the writer can read
 * under What Write will send. Never two ranked prompts. No silent winner.
 * What happens comes from live PlotTrack cells. Book Notes are standing
 * reference when present.
 */

function rememberedSceneId(storyId) {
  if (!storyId) return '';
  return readLastScene(storyId) || readSceneWrite(storyId)?.sceneId || '';
}

/**
 * The scene Write should open when this visit did not name one.
 * An explicit ?scene= wins. The path's next scene applies only when
 * the address did not name a scene. No path yet: keep the bookmark.
 */
export function sceneToOpen(path, rememberedId, urlSceneId = '') {
  const remembered = rememberedId ? String(rememberedId) : '';
  const fromUrl = urlSceneId ? String(urlSceneId) : '';
  if (fromUrl) return fromUrl;
  const fallback = remembered;
  const scenes = Array.isArray(path?.scenes) ? path.scenes : [];
  if (!scenes.length) return fallback;
  const named = path.next_scene;
  const namedOpen = named && named.accepted !== true
    ? (scenes.find((scene) => String(scene.id) === String(named.id)) || named)
    : null;
  const next = namedOpen || scenes.find((scene) => scene.accepted !== true) || null;
  if (!next?.id) return fallback;
  return String(next.id);
}

/**
 * An explicit ?scene= is the scene to open, with or without a title.
 * With no pin, every device asks compose for the server's next scene,
 * including a phone that has never stored a bookmark. A remembered id
 * is only the fallback the server uses when every scene is accepted.
 * An explicit link is pinned. An explicit pick pins too, and a pending
 * path correction cannot replace it.
 */
export function openingChoice(handoff, storyId) {
  const remembered = rememberedSceneId(storyId);
  const address = sceneAddress(handoff?.sceneId);
  if (address.label) {
    return {
      sceneId: '',
      waitForPath: false,
      pinned: false,
      correctTo: '',
      remembered,
      urlSceneId: '',
      sceneLabel: address.label,
    };
  }
  if (address.id) {
    const sceneId = address.id;
    return {
      sceneId,
      waitForPath: false,
      pinned: true,
      correctTo: '',
      remembered,
      urlSceneId: sceneId,
      sceneLabel: '',
    };
  }
  return {
    sceneId: '',
    waitForPath: true,
    pinned: false,
    correctTo: '',
    remembered,
    urlSceneId: '',
    sceneLabel: '',
  };
}
/** The room chat mounts this node; proposed note updates render there. */
function useChatNoteHost() {
  const [host, setHost] = useState(null);
  useEffect(() => {
    const sync = () => setHost(document.querySelector('[data-chat-note-offers]'));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  return host;
}

export default function SceneWritingPage() {
  const { storyId: routeStoryId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const handoff = sceneWritingHandoffFromSearch(searchParams);
  const { currentProject, currentStory, setProjectAndStory, storyListVersion } = useApp();
  const navigate = useNavigate();

  const openedId = routeStoryId || currentStory?.id || null;
  const { storyId, loadedStory } = useSyncStoryContext({ routeStoryId: openedId });

  const noteHost = useChatNoteHost();
  // Settings → Appearance → Text Size writes `zoomLevel`. Chat applies it
  // when the room chat mounts; this page applies the same key so prose
  // and panels scale when the writer opens scene writing on its own.
  useEffect(() => {
    const applyZoom = (raw) => {
      const next = readChatZoom(raw);
      document.documentElement.style.setProperty('--zoom-level', String(next / 100));
    };
    applyZoom(localStorage.getItem(CHAT_TEXT_SIZE.storageKey));
    const onStorage = (event) => {
      if (event.key != null && event.key !== CHAT_TEXT_SIZE.storageKey) return;
      applyZoom(event.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [projectStories, setProjectStories] = useState(null);
  const [compose, setCompose] = useState(null);
  // Run 51: a bookmark (last scene, or the ?scene= this page wrote) gives way
  // to the path's next scene before compose. A titled handoff stays.
  const resumedRef = useRef(false);
  const openingRef = useRef(openingChoice(handoff, routeStoryId));
  const [resumeNote, setResumeNote] = useState('');
  const [sceneId, setSceneId] = useState(() => openingRef.current.sceneId);
  const [pickCardId, setPickCardId] = useState('');
  const [instruction, setInstruction] = useState('');
  const [resolutions, setResolutions] = useState([]);
  const [prose, setProse] = useState('');
  const [draftCopied, setDraftCopied] = useState(false);
  const draftCopyTimer = useRef(null);
  const [serverProse, setServerProse] = useState('');
  const [offers, setOffers] = useState([]);
  const [editingOfferId, setEditingOfferId] = useState('');
  const [editDraft, setEditDraft] = useState('');
  const [passStatus, setPassStatus] = useState('draft');
  const [editingAccepted, setEditingAccepted] = useState(false);
  const [restoreAsk, setRestoreAsk] = useState(false);
  const [passId, setPassId] = useState('');
  const [incomplete, setIncomplete] = useState(false);
  const [loading, setLoading] = useState(Boolean(storyId));
  const [busy, setBusy] = useState(false);
  // `busy` only dims Accept draft. This is the Saving… label, and it
  // blocks a second click before that render lands.
  const [accepting, setAccepting] = useState(false);
  const acceptingRef = useRef(false);
  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();
  const [error, setError] = useState(null);
  const [sessionFailure, setSessionFailure] = useState(false);
  const [failureDismissed, setFailureDismissed] = useState(false);
  const sawWritingRef = useRef(false);
  const [whoseHead, setWhoseHead] = useState(() => headFromCompose(null, handoff));
  const [personOverride, setPersonOverride] = useState(null);
  const [bookPov, setBookPov] = useState(null);
  const [modelNames, setModelNames] = useState(null);
  const [povPinned, setPovPinned] = useState(false);
  const [personNote, setPersonNote] = useState('');
  const [rewriteNote, setRewriteNote] = useState('');
  const [writeConfirm, setWriteConfirm] = useState(false);
  const [newDraftAsk, setNewDraftAsk] = useState(false);
  const [allowancePause, setAllowancePause] = useState(false);
  const [contextOpen, setContextOpen] = useState(readContextOpen);
  const isPhone = useBreakpoint() === 'mobile';
  const [phoneSettingsOpen, setPhoneSettingsOpen] = useState(false);
  const { path: bookPath, status: bookPathStatus } = useBookPath(storyId);
  const notePathChanged = () => noteBookPathChanged();
  const [draftEstimate, setDraftEstimate] = useState(null);
  const [noteFactEstimate, setNoteFactEstimate] = useState(null);
  const [noteOffersOn, setNoteOffersOn] = useState(true);
  const [acceptCostReady, setAcceptCostReady] = useState(false);
  const onAcceptCost = useCallback((ready) => setAcceptCostReady(Boolean(ready)), []);
  const [estimatePending, setEstimatePending] = useState(false);
  const [actionNote, setActionNote] = useState('');
  const [confirmNotice, setConfirmNotice] = useState('');
  const [openingSlow, setOpeningSlow] = useState(false);
  const estimateToken = useRef(0);
  const estimateReady = useRef(new Map());
  const estimateInflight = useRef(new Map());
  const [settledInstruction, setSettledInstruction] = useState(instruction);
  const proseEditRef = useRef(null);
  const fullSurfaceArmed = useRef(false);
  // Last scene the page actually showed. The draft dialog must not
  // swap that for "Choose a scene" / "Loading this scene…" while a
  // later read is still out.
  const heldScenePaint = useRef(null);
  const activeProjectId = currentProject?.id || loadedStory?.project_id || null;
  const whoseCastIds = castIdsFromBookPov(bookPov);

  useEffect(() => () => {
    window.clearTimeout(draftCopyTimer.current);
  }, []);

  useEffect(() => {
    if (!styleRoomEnabled()) return undefined;
    let cancelled = false;
    modelsApi.getAvailable()
      .then((res) => {
        if (!cancelled) setModelNames(modelMenuNames(res?.models || res));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!storyId) {
      setBookPov(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await styleCardsApi.list(storyId);
        if (!cancelled) setBookPov(list?.book_pov || null);
      } catch {
        if (!cancelled) setBookPov(null);
      }
    })();
    return () => { cancelled = true; };
  }, [storyId]);

  useEffect(() => {
    if (!activeProjectId) { setProjectStories(null); return undefined; }
    let cancelled = false;
    storiesApi.list(activeProjectId)
      .then((res) => { if (!cancelled) setProjectStories(unwrapList(res)); })
      .catch(() => { if (!cancelled) setProjectStories([]); });
    return () => { cancelled = true; };
  }, [activeProjectId, storyListVersion]);

  useEffect(() => {
    if (routeStoryId) { setPickerOpen(false); return; }
    if (currentStory?.id) {
      setPickerOpen(false);
      navigate(sceneWritingPath(currentStory.id), { replace: true });
      return;
    }
    if (!currentProject) { setPickerOpen(true); return; }
    if (!projectStories || projectStories.length === 0) return;
    const mostRecent = [...projectStories].sort(byRecency)[0];
    setProjectAndStory(currentProject, mostRecent);
    navigate(sceneWritingPath(mostRecent.id), { replace: true });
  }, [routeStoryId, currentStory?.id, currentProject, projectStories, navigate, setProjectAndStory]);

  const sceneIdRef = useRef(sceneId);
  sceneIdRef.current = sceneId;
  const passIdRef = useRef(passId);
  passIdRef.current = passId;
  const instructionRef = useRef(instruction);
  instructionRef.current = instruction;
  const flushDraftRef = useRef(async () => true);
  const confirmWriteRef = useRef(async () => false);
  const switchSceneRef = useRef(() => {});
  // The priced scene. A later compose that only moves the pass or the
  // heartbeat is the same draft. A different signature is a different draft.
  const confirmBasisRef = useRef('');
  const writeConfirmRef = useRef(false);
  const genderChoicesRef = useRef(null);
  const writeInFlightRef = useRef(false);
  const newDraftNextRef = useRef(null);
  const [confirming, setConfirming] = useState(false);
  writeConfirmRef.current = writeConfirm;
  const adoptDraft = useCallback((text) => {
    const next = text ?? '';
    setServerProse(next);
    setProse(next);
  }, []);
  // True once the writer edits the box for the scene now on screen.
  // A late compose must not put the server instruction back in.
  const instructionEdited = useRef(false);
  // #1710: which scene's stored notes have already been read into the box.
  // The persist effect must not run for a scene before that read.
  const instructionHydratedFor = useRef('');
  const loadGen = useRef(0);
  const loadAbortRef = useRef(null);
  const sceneOnScreenRef = useRef(false);
  const loadedKey = useRef('');
  const inflightKey = useRef('');
  // When the compose now on screen was applied. A later point stamp is newer.
  const composeAppliedAt = useRef(0);
  // One extra read per point. Stops the stamp check from asking forever.
  const voiceRecheckedAt = useRef(0);
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const leaveAfterShrinkRef = useRef(false);

  const applyCompose = useCallback((next, { recoverPass = false, keepInstruction = false } = {}) => {
    const incomingScene = next?.scene_id ? String(next.scene_id) : '';
    const here = sceneIdRef.current ? String(sceneIdRef.current) : '';
    const pinnedId = openingRef.current.pinned ? String(openingRef.current.sceneId || '') : '';
    // A compose for another scene must not land on a pinned pick or the scene on screen.
    if (incomingScene && pinnedId && incomingScene !== pinnedId) return false;
    if (incomingScene && here && incomingScene !== here) return false;

    setCompose(next);
    sceneOnScreenRef.current = true;
    setPersonOverride(null);
    setWhoseHead(headFromCompose(next, sceneWritingHandoffFromSearch(searchParamsRef.current)));
    const picked = firstSceneId(next);
    if (picked && !sceneIdRef.current) {
      sceneIdRef.current = picked;
      setSceneId(picked);
    }

    // #1467: the box is only what the writer typed for this scene.
    // Never refill it from last_pass.merged_instruction or the composed
    // instruction (that is where "POV: … (from scene)." came back).
    if (keepInstruction || instructionEdited.current) {
      // The note they still have, including an empty box they cleared.
    } else {
      const forScene = sceneIdRef.current || picked;
      const stored = readStoredInstruction(storyId, forScene);
      const showing = String(instructionRef.current || '');
      // #1710: a compose must not blank a note that is already on screen
      // when storage is empty. An empty key is a clear only after the
      // writer edited the box (instructionEdited above).
      if (!(showing.trim() && !String(stored || '').trim())) {
        setInstruction(stored);
      }
    }

    const nextPass = next?.last_pass || {};
    const incomingId = nextPass.id || next?.pass_id || '';
    const prevId = passIdRef.current;
    const passChanged = Boolean(prevId && incomingId && String(prevId) !== String(incomingId));
    // A new pass must not replace unsaved words on the one the writer was editing.
    if (!recoverPass && passChanged) {
      void flushDraftRef.current();
    }
    if (recoverPass || passChanged) {
      adoptDraft(passProseRaw(next));
    }
    const status = nextPass.status || (next?.accepted ? 'accepted' : 'draft');
    if ((recoverPass || passChanged) && status !== 'accepted') {
      // Words typed before a reload that the server never received.
      const local = readUnsavedDraft(
        storyId,
        incomingScene || sceneIdRef.current,
        incomingId,
        passProseRaw(next),
      );
      if (local) setProse(local);
    }
    const reason = String(nextPass.finish_reason || next?.finish_reason || '').toLowerCase();
    const stopped = nextPass.incomplete === true
      || next?.incomplete === true
      || reason === 'length'
      || reason === 'max_tokens';
    setPassId(nextPass.id || next?.pass_id || '');
    setPassStatus(status);
    setIncomplete(status !== 'accepted' && stopped);
    const offersOn = next?.note_offers_enabled !== false;
    setNoteOffersOn(offersOn);
    setOffers(offersOn ? (next?.pending_note_offers || []) : []);
    setEditingOfferId('');
    setEditDraft('');
  }, [storyId, adoptDraft]);

  const load = useCallback(async (nextSceneId, {
    recoverPass = true,
    force = false,
    openNext = false,
    background = false,
    sceneLabel = '',
  } = {}) => {
    if (!storyId) return;
    const label = String(sceneLabel || '').trim();
    const key = openNext ? `${storyId}:next` : `${storyId}:${label || nextSceneId || ''}`;
    if (!force && inflightKey.current === key) return;
    const gen = loadGen.current + 1;
    loadGen.current = gen;
    inflightKey.current = key;
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    // A tab return refetches compose. The scene already on screen stays,
    // including its POV, so the chat stream is not covered by a loading page.
    const keepOnScreen = background && sceneOnScreenRef.current;
    if (!keepOnScreen) setLoading(true);
    setError(null);
    const started = Date.now();
    try {
      const payload = await sceneWritingApi.compose(storyId, {
        sceneId: label ? undefined : (nextSceneId || undefined),
        sceneLabel: label || undefined,
        openNext,
        signal: controller.signal,
      });
      if (gen !== loadGen.current) return;
      const payloadScene = payload?.scene_id ? String(payload.scene_id) : '';
      const pinnedId = openingRef.current.pinned ? String(openingRef.current.sceneId || '') : '';
      // A late open-next for another scene must not replace a pick or a handoff.
      if (pinnedId && payloadScene && payloadScene !== pinnedId) return;
      const correctTo = (!pinnedId && openingRef.current.correctTo)
        ? String(openingRef.current.correctTo)
        : '';
      const resolvedPreview = (!openNext && nextSceneId)
        || payloadScene
        || (payload?.scenes?.[0]?.id ? String(payload.scenes[0].id) : '');
      // The path named a different scene than this compose. Drop the prose
      // so the header cannot say one scene while the body shows another.
      if (correctTo && correctTo !== String(resolvedPreview)) {
        openingRef.current.correctTo = '';
        adoptDraft('');
        setPassStatus('draft');
        setPassId('');
        setIncomplete(false);
        loadedKey.current = '';
        if (String(sceneIdRef.current || '') !== correctTo) {
          sceneIdRef.current = correctTo;
          setSceneId(correctTo);
        }
        const wanted = correctTo;
        queueMicrotask(() => {
          if (openingRef.current.pinned) return;
          if (String(sceneIdRef.current || '') !== wanted) return;
          void load(wanted, { force: true });
        });
        return;
      }
      const applied = applyCompose(payload, { recoverPass });
      if (applied === false) return;
      if (label) openingRef.current.sceneLabel = '';
      const resolvedId = (!openNext && nextSceneId) || payload?.scene_id || payload?.scenes?.[0]?.id || '';
      loadedKey.current = `${storyId}:${resolvedId}`;
      composeAppliedAt.current = Date.now();
      // The point can commit while this read is still out. The response
      // then has no card, and loadedKey would keep that "No voice yet".
      const pointed = readSceneVoicePoint(storyId);
      const resolvedKey = String(resolvedId || '');
      if (
        pointed
        && sceneVoicePointedAfter(storyId, resolvedKey, started)
        && voiceRecheckedAt.current !== pointed.at
      ) {
        voiceRecheckedAt.current = pointed.at;
        queueMicrotask(() => {
          if (String(sceneIdRef.current || '') !== resolvedKey) return;
          void load(resolvedKey, {
            force: true,
            recoverPass: false,
            background: sceneOnScreenRef.current,
          });
        });
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      if (gen !== loadGen.current) return;
      if (isRawSceneError(err?.message)) {
        setError(null);
        if (!openNext) {
          openingRef.current = {
            ...openingRef.current,
            pinned: false,
            waitForPath: false,
            sceneId: '',
            sceneLabel: '',
          };
          // Drop the bad address so the next-scene compose can land.
          // Leave scene state until that compose returns, or this effect
          // starts a second read.
          sceneIdRef.current = '';
          queueMicrotask(() => {
            if (openingRef.current.pinned) return;
            if (String(sceneIdRef.current || '')) return;
            void load(undefined, { openNext: true, force: true });
          });
        }
        return;
      }
      // A read that dies while Confirm is up, or while this write is in
      // flight, is the page moving under the dialog. The write's own result
      // is the only failure that belongs on screen.
      if (writeInFlightRef.current || writeConfirmRef.current) return;
      setError(err?.message || 'Could not open scene writing.');
    } finally {
      if (gen === loadGen.current) {
        inflightKey.current = '';
        if (!keepOnScreen) setLoading(false);
      } else if (!keepOnScreen) {
        // A newer read owns the flag only while its request is still out.
        // An abort that cleared the key, or aborted the replacement, used
        // to leave loading true after the form had already rendered.
        const replacement = loadAbortRef.current;
        const replacementLive = Boolean(inflightKey.current)
          && replacement
          && !replacement.signal.aborted;
        if (!replacementLive) setLoading(false);
      }
    }
  }, [storyId, applyCompose, adoptDraft]);

  const loadRef = useRef(load);
  loadRef.current = load;

  const [boundStoryId, setBoundStoryId] = useState(storyId);
  // A missing id for one frame is not a different book. Clearing compose
  // here is what put POV on "Loading…" and the page on "Loading this scene…".
  if (storyId && boundStoryId !== storyId) {
    const choice = openingChoice(handoff, storyId);
    openingRef.current = choice;
    setBoundStoryId(storyId);
    sceneOnScreenRef.current = false;
    resumedRef.current = false;
    setResumeNote('');
    setSceneId(choice.sceneId);
    setResolutions([]);
    setProse('');
    setServerProse('');
    setOffers([]);
    setPassStatus('draft');
    setPassId('');
    setIncomplete(false);
    setCompose(null);
    setError(null);
    setLoading(Boolean(storyId));
    instructionEdited.current = false;
    instructionHydratedFor.current = '';
    loadedKey.current = '';
    inflightKey.current = '';
    loadGen.current += 1;
  }

  // #1710: read this scene's notes before effects persist. An empty box on
  // the first paint used to delete the stored note, so Add and use (and any
  // later compose) opened a blank box.
  const instructionKey = storyId && sceneId ? `${storyId}.${sceneId}` : '';
  if (instructionKey && instructionHydratedFor.current !== instructionKey) {
    instructionHydratedFor.current = instructionKey;
    if (!instructionEdited.current) {
      const stored = readStoredInstruction(storyId, sceneId);
      if (stored !== instruction) setInstruction(stored);
    }
  }

  useEffect(() => {
    if (!storyId) return undefined;
    if (openingRef.current.waitForPath) return undefined;
    if (openingRef.current.sceneLabel && !sceneId) return undefined;
    const key = `${storyId}:${sceneId || ''}`;
    const inflight = inflightKey.current;
    // The path effect, or the open-next compose, already started this read.
    if (inflight === key || inflight === `${storyId}:next`) return undefined;
    // Coming back before the other scene's compose finishes still has this
    // key marked loaded. Fetch again, or that late response is all we get.
    if (loadedKey.current === key && !inflight) return undefined;
    // Depend on the ids, not on load's identity. A new callback must not
    // abort the compose that is already painting this scene.
    loadRef.current(sceneId || undefined);
    return () => {
      loadAbortRef.current?.abort();
      // The aborted request clears inflightKey only in a later finally.
      // Clear it now so this effect's restart is not skipped (#1555).
      if (inflightKey.current === key) inflightKey.current = '';
    };
  }, [sceneId, storyId]);

  // A Notes edit in another tab, or a restored page, must not keep the
  // compose from before that edit (#1470). A scene pointed at a voice is
  // the same kind of miss: Write keeps the compose from this visit, and a
  // full reload was the only later read.
  const busyRef = useRef(false);
  busyRef.current = busy;
  useEffect(() => {
    if (!storyId) return undefined;
    const refresh = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (busyRef.current) return;
      // The path has not named a scene yet. Once one is on screen, a tab
      // return still refetches it without taking the page down.
      if (openingRef.current.waitForPath && !sceneOnScreenRef.current) return;
      load(sceneIdRef.current || undefined, { force: true, recoverPass: false, background: true });
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const onPageShow = (event) => {
      if (event.persisted) refresh();
    };
    const onFocus = () => {
      // The document never went hidden (another window beside this one).
      // Skip the first paint; that read is still the open.
      if (!sceneOnScreenRef.current) return;
      const here = sceneIdRef.current ? String(sceneIdRef.current) : '';
      if (!sceneVoicePointedAfter(storyId, here, composeAppliedAt.current)) return;
      refresh();
    };
    const onPointed = (detail) => {
      const incomingStory = detail?.storyId ? String(detail.storyId) : '';
      if (incomingStory && incomingStory !== String(storyId)) return;
      const pointed = detail?.sceneId ? String(detail.sceneId) : '';
      const here = sceneIdRef.current ? String(sceneIdRef.current) : '';
      if (!pointed || (here && pointed !== here)) return;
      if (busyRef.current) return;
      load(here || pointed, {
        force: true,
        recoverPass: false,
        background: sceneOnScreenRef.current,
      });
    };
    const onPointEvent = (event) => onPointed(event?.detail);
    const onStorage = (event) => {
      if (event.key !== sceneVoicePointKey(storyId)) return;
      let detail = null;
      try { detail = JSON.parse(event.newValue || 'null'); } catch { detail = null; }
      onPointed(detail);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onFocus);
    window.addEventListener(SCENE_VOICE_POINTED, onPointEvent);
    window.addEventListener('storage', onStorage);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(SCENE_VOICE_POINTED, onPointEvent);
      window.removeEventListener('storage', onStorage);
    };
  }, [storyId, load]);

  // Reload often lands while preparation is still running. Compose then
  // reports the previous pass as done, and the buttons are grey because
  // the scene is still opening — not because generation.kind is writing.
  // The sentence follows that same visit, remembered when Write was confirmed.
  const storedFollow = readSceneWrite(storyId);
  const followFlag = storedFollow && (
    !sceneId || String(storedFollow.sceneId || '') === String(sceneId)
  ) ? storedFollow : null;
  const rawGenerationView = followSceneWrite({
    generation: compose?.generation,
    flag: followFlag,
    passId: passId || compose?.last_pass?.id || compose?.pass_id || '',
  });
  const rawKind = rawGenerationView.kind;
  useEffect(() => {
    sawWritingRef.current = false;
    setSessionFailure(false);
    setFailureDismissed(false);
  }, [storyId, sceneId]);
  useEffect(() => {
    if (failureDismissed) return;
    if (rawKind === 'writing') {
      sawWritingRef.current = true;
      return;
    }
    if (rawKind === 'stopped' && sawWritingRef.current) {
      setSessionFailure(true);
      return;
    }
    if (rawKind === 'idle' && sawWritingRef.current) {
      sawWritingRef.current = false;
      setSessionFailure(false);
    }
  }, [rawKind, failureDismissed]);
  const generationView = presentSceneWrite(rawGenerationView, {
    sessionFailure: sessionFailure && !failureDismissed,
  });
  const generationKind = generationView.kind;
  const followStarted = followFlag?.startedAt || 0;
  useEffect(() => {
    if (!storyId || !followStarted) return;
    if (generationKind !== 'idle') return;
    clearSceneWrite(storyId);
  }, [storyId, followStarted, generationKind]);
  useEffect(() => {
    if (generationKind !== 'writing' || busy || !storyId) return undefined;
    const used = sceneId || firstSceneId(compose);
    if (!used) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    const timer = setInterval(async () => {
      try {
        const payload = await sceneWritingApi.compose(storyId, {
          sceneId: used,
          signal: controller.signal,
        });
        if (cancelled) return;
        const nextKind = sceneGenerationView(payload?.generation).kind;
        if (nextKind !== 'writing') {
          applyCompose(payload, { recoverPass: true, keepInstruction: true });
        } else {
          setCompose((prev) => (prev ? { ...prev, generation: payload?.generation } : prev));
        }
      } catch {
        /* keep the line; the next read tries again */
      }
    }, SCENE_WRITE_POLL_MS);
    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(timer);
    };
  }, [generationKind, busy, storyId, sceneId, compose, applyCompose]);

  useEffect(() => {
    if (compose && !sceneId) {
      const picked = firstSceneId(compose);
      if (picked) {
        sceneIdRef.current = picked;
        setSceneId(picked);
      }
    }
  }, [compose, sceneId]);

  useEffect(() => {
    if (!storyId || !sceneId) return;
    const key = `${storyId}.${sceneId}`;
    if (instructionHydratedFor.current !== key) return;
    // An untouched empty box is not a clear. Deleting here wiped notes
    // that this visit had not read back yet (#1710).
    if (!instructionEdited.current && !String(instruction || '').trim()) return;
    writeStoredInstruction(storyId, sceneId, instruction);
  }, [storyId, sceneId, instruction]);

  // A bookmark used to wait for GET /next-step and only then compose.
  // That extra round trip is the open going from about 1.5s to about 2.4s.
  // Compose asks for the next scene in the same request. The path still
  // supplies the resume line, and a disagreeing path replaces the scene.
  useEffect(() => {
    const label = openingRef.current.sceneLabel;
    if (!label || !storyId || openingRef.current.pinned) return undefined;
    load(undefined, { sceneLabel: label });
    return () => {
      loadAbortRef.current?.abort();
      if (inflightKey.current === `${storyId}:${label}`) inflightKey.current = '';
    };
  }, [load, storyId]);

  useEffect(() => {
    setError((current) => (isRawSceneError(current) ? null : current));
  }, [searchParams]);

  useEffect(() => {
    if (!openingRef.current.waitForPath || !storyId) return undefined;
    const key = `${storyId}:next`;
    if (inflightKey.current === key) return undefined;
    const bookmark = openingRef.current.urlSceneId || openingRef.current.remembered || undefined;
    load(bookmark, { openNext: true });
    return () => {
      loadAbortRef.current?.abort();
      if (inflightKey.current === key) inflightKey.current = '';
    };
  }, [load, storyId]);

  useEffect(() => {
    if (openingRef.current.pinned) return;
    // The cost dialog is buying one scene. A path read must not swap it,
    // including while that write is still in flight.
    if (writeConfirmRef.current || writeInFlightRef.current) return;
    if (!openingRef.current.waitForPath || !storyId) return;
    if (bookPathStatus === 'loading') return;
    const remembered = openingRef.current.remembered;
    const urlSceneId = openingRef.current.urlSceneId || '';
    if (!bookPath) {
      // A failed path read must not open this device's bookmark.
      // Phone and desktop would show different scenes. The open-next
      // compose is the server's next scene on every device.
      openingRef.current = { ...openingRef.current, waitForPath: false, correctTo: '' };
      return;
    }
    const target = sceneToOpen(bookPath, remembered, urlSceneId);
    const scenes = Array.isArray(bookPath.scenes) ? bookPath.scenes : [];
    const here = scenes.find((scene) => String(scene.id) === String(remembered));
    const next = scenes.find((scene) => String(scene.id) === String(target)) || bookPath.next_scene;
    const opened = sceneIdRef.current ? String(sceneIdRef.current) : '';
    const stillOpening = inflightKey.current === `${storyId}:next`;
    openingRef.current = {
      ...openingRef.current,
      sceneId: target || '',
      waitForPath: false,
      correctTo: stillOpening && target ? String(target) : '',
    };
    resumedRef.current = false;
    if (target && here?.accepted === true && String(target) !== String(remembered)) {
      setResumeNote(RESUME_PAST_ACCEPTED(here?.name || 'That scene', next?.name || 'the next scene'));
    }
    if (!target) return;
    if (stillOpening || opened === String(target) || loadedKey.current === `${storyId}:${target}`) return;
    if (String(sceneIdRef.current || '') !== String(target)) {
      adoptDraft('');
      setPassStatus('draft');
      setPassId('');
      setIncomplete(false);
    }
    setSceneId(String(target));
    void load(String(target));
  }, [bookPath, bookPathStatus, storyId, load, adoptDraft, writeConfirm]);

  useEffect(() => {
    const handle = window.setTimeout(() => setSettledInstruction(instruction), ESTIMATE_SETTLE_MS);
    return () => window.clearTimeout(handle);
  }, [instruction]);

  const saveOpenPass = useCallback(async (id, body, opts) => {
    const saved = opts
      ? await sceneWritingApi.saveDraft(id, body, opts)
      : await sceneWritingApi.saveDraft(id, body);
    if ((saved?.status || '') === 'accepted') {
      setCompose((prev) => {
        if (!prev?.last_pass) return prev;
        return {
          ...prev,
          last_pass: {
            ...prev.last_pass,
            generated_text: saved.generated_text ?? body.generatedText ?? prev.last_pass.generated_text,
            status: 'accepted',
            hand_edited_at: saved.hand_edited_at || prev.last_pass.hand_edited_at,
            has_accepted_original: saved.has_accepted_original === true
              || prev.last_pass.has_accepted_original === true,
            notes_updated_at: saved.notes_updated_at ?? prev.last_pass.notes_updated_at ?? null,
            edited_since_notes: Boolean(saved.edited_since_notes),
          },
        };
      });
      if (saved?.generated_text != null) setServerProse(saved.generated_text);
    }
    return saved;
  }, []);

  const draftSave = useSceneDraftAutosave({
    active: passStatus !== 'accepted' || editingAccepted,
    storyId,
    sceneId,
    passId,
    text: prose,
    serverText: serverProse,
    saveDraft: saveOpenPass,
    onOpenScene: (nextId) => switchSceneRef.current(nextId),
    guardAcceptedShrink: passStatus === 'accepted',
  });
  flushDraftRef.current = draftSave.flush;

  useEffect(() => {
    setEditingAccepted(false);
    setRestoreAsk(false);
    setPovPinned(false);
    leaveAfterShrinkRef.current = false;
    newDraftNextRef.current = null;
    setNewDraftAsk(false);
  }, [sceneId, passId]);

  const openUnacceptedDraft = passStatus !== 'accepted';
  const showProseEditor = openUnacceptedDraft || editingAccepted;
  const acceptedHandEdited = passStatus === 'accepted' && (
    compose?.last_pass?.has_accepted_original === true
    || Boolean(compose?.last_pass?.hand_edited_at)
    || (editingAccepted && prose !== serverProse)
  );
  const writerEditedFlag = compose?.last_pass?.writer_edited;
  const replacesEditedDraft = openUnacceptedDraft && (
    writerEditedFlag === true
    || writerEditedFlag === 'true'
    || prose !== serverProse
  );
  const keptEdit = openUnacceptedDraft
    ? String(compose?.last_pass?.replaced_edited_text || '')
    : '';
  const showBringBack = Boolean(keptEdit.trim()) && keptEdit !== prose;
  const bringBackPending = useRef(false);
  const flushDraft = draftSave.flush;
  const onBringBackEdited = () => {
    if (!keptEdit) return;
    bringBackPending.current = true;
    setProse(keptEdit);
  };
  useEffect(() => {
    if (!bringBackPending.current || prose !== keptEdit) return;
    bringBackPending.current = false;
    void flushDraft();
  }, [prose, keptEdit, flushDraft]);

  // Reload reopens this scene. The URL is the record; last-viewed covers
  // a later visit that arrives without ?scene=.
  useEffect(() => {
    if (!storyId || !sceneId) return;
    rememberLastScene(storyId, sceneId);
    const current = searchParams.get('scene') || '';
    if (current === String(sceneId)) return;
    const next = new URLSearchParams(searchParams);
    next.set('scene', String(sceneId));
    setSearchParams(next, { replace: true });
  }, [storyId, sceneId, searchParams, setSearchParams]);

  const stillOpening = Boolean(loading || (storyId && !sceneId && !compose && !error));
  useEffect(() => {
    if (!stillOpening) {
      setOpeningSlow(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setOpeningSlow(true), OPENING_SLOW_MS);
    return () => window.clearTimeout(timer);
  }, [stillOpening, sceneId]);

  const fullSurface = Boolean(prose.trim() || passId);
  useEffect(() => {
    if (!fullSurface) {
      fullSurfaceArmed.current = false;
      return;
    }
    if (fullSurfaceArmed.current) return;
    if (passStatus === 'accepted' || busy) return;
    focusWholeDraft(proseEditRef.current);
    fullSurfaceArmed.current = true;
  }, [fullSurface, passStatus, busy]);

  const toggleContext = () => {
    setContextOpen((open) => {
      const next = !open;
      try {
        sessionStorage.setItem(CONTEXT_OPEN_KEY, next ? '1' : '0');
      } catch {
        /* ignore quota / private mode */
      }
      return next;
    });
  };

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(err?.message || 'That scene-writing action failed.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const onStopEditing = async () => {
    // A shrink that was already refused must not ask again. Stop editing
    // leaves the editor and puts the saved scene back on screen.
    const kept = await draftSave.flush();
    if (kept === 'held') {
      leaveAfterShrinkRef.current = true;
      return;
    }
    leaveAfterShrinkRef.current = false;
    if (kept === 'declined') {
      removeUnsavedDraft(storyId, sceneId, passId);
      setProse(serverProse);
      setEditingAccepted(false);
      return;
    }
    if (kept === true) setEditingAccepted(false);
  };
  const stopEditingRef = useRef(onStopEditing);
  stopEditingRef.current = onStopEditing;

  useEffect(() => {
    if (!editingAccepted) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      if (draftSave.shrinkAsk != null) return;
      event.preventDefault();
      void stopEditingRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editingAccepted, draftSave.shrinkAsk]);

  const onRestoreAccepted = () => {
    setRestoreAsk(true);
  };

  const confirmRestoreAccepted = async () => {
    setRestoreAsk(false);
    const next = await run(() => sceneWritingApi.restoreAccepted(storyId, {
      sceneId,
      passId,
    }));
    if (!next) return;
    adoptDraft(next.generated_text ?? '');
    setEditingAccepted(false);
    setPassStatus('accepted');
    setCompose((prev) => ({
      ...(prev || {}),
      last_pass: {
        ...(prev?.last_pass || {}),
        generated_text: next.generated_text ?? '',
        status: 'accepted',
        accepted_at: next.accepted_at ?? prev?.last_pass?.accepted_at,
        hand_edited_at: null,
        has_accepted_original: false,
        notes_updated_at: next.notes_updated_at ?? prev?.last_pass?.notes_updated_at ?? null,
        edited_since_notes: false,
      },
    }));
  };

  useEffect(() => {
    if (!editingAccepted) return undefined;
    proseEditRef.current?.focus();
    return undefined;
  }, [editingAccepted]);

  const handlePick = useCallback((project, story) => {
    setProjectAndStory(project, story);
    setPickerOpen(false);
    navigate(sceneWritingPath(story.id));
  }, [setProjectAndStory, navigate]);

  const onResolve = async (fight, choice) => {
    const next = await run(() => sceneWritingApi.resolve(storyId, {
      sceneId,
      fightId: fight.id,
      choice,
      editedInstruction: choice === 'edit' ? instruction : null,
      resolutions,
    }));
    if (!next) return;
    applyCompose(next, { recoverPass: false });
    setResolutions((prev) => {
      const others = prev.filter((item) => item.fight_id !== fight.id);
      return [...others, {
        fight_id: fight.id,
        choice,
        edited_instruction: choice === 'edit' ? instruction : null,
      }];
    });
  };

  const recoverThisWrite = async (priorKey, usedSceneId) => {
    if (!storyId || !usedSceneId) return null;
    try {
      const payload = await sceneWritingApi.compose(storyId, { sceneId: usedSceneId });
      const recovered = passProse(payload);
      const nextKey = passKey(payload);
      // A new pass after the model ran. Same old pass is not this Write.
      if (recovered && nextKey && nextKey !== priorKey) {
        applyCompose(payload, { recoverPass: true, keepInstruction: true });
        return payload;
      }
      return null;
    } catch {
      return null;
    }
  };

  const onSetPerson = async (mode) => {
    const usedSceneId = sceneId || firstSceneId(compose);
    if (!storyId || !usedSceneId) return;
    setPersonOverride(mode || '');
    setPersonNote('');
    try {
      // Run 9: the book's narrative POV was never set, so every scene reset to
      // "Inherited" and Close third was picked 5 times. The first pick, while
      // the book is Not set (not "Don't know yet"), becomes the book's; this
      // scene then inherits it. Any scene can still differ.
      if (mode && bookPov && !bookPov.mode && !bookPov.deferred) {
        const saved = await styleCardsApi.setBookPov(storyId, {
          povMode: mode,
          povEntityId: bookPov.entity_id || null,
        });
        setBookPov(saved?.mode ? saved : { ...bookPov, mode, status: 'set' });
        await styleCardsApi.setSectionStyle(storyId, usedSceneId, sectionPersonBody(null));
        setPersonNote(PERSON_BOOK_DEFAULT_SET(personModeLabel(mode)));
      } else {
        await styleCardsApi.setSectionStyle(storyId, usedSceneId, sectionPersonBody(mode));
      }
      const payload = await sceneWritingApi.compose(storyId, { sceneId: usedSceneId });
      applyCompose(payload, { keepInstruction: true });
    } catch (err) {
      setPersonOverride(null);
      setError(err?.message || PERSON_SAVE_FAILED);
    }
  };

  const onSetWhoseHead = async (next) => {
    setPovPinned(true);
    setWhoseHead(next);
    const usedSceneId = sceneId || firstSceneId(compose);
    if (!storyId || !usedSceneId) return;
    try {
      await plottrackApi.setWhoseHead(storyId, usedSceneId, {
        choice: next.choice,
        entityId: next.entityId,
        entityName: next.entityName,
      });
      const payload = await sceneWritingApi.compose(storyId, { sceneId: usedSceneId });
      applyCompose(payload, { keepInstruction: true });
    } catch (err) {
      setError(err?.message || 'Could not save the POV character.');
    }
  };

  // Run 43: name the POV character here instead of leaving for Notes.
  // The writer typed it, so it is born a character note (no review step).
  const onAddPovPerson = async (name) => {
    const created = await entitiesApi.createForStory(storyId, {
      name,
      entity_type: 'character',
    });
    await onSetWhoseHead({
      choice: WHOSE_HEAD_CHOICE_PERSON,
      entityId: String(created.id),
      entityName: created.name || name,
    });
  };

  const applyEstimatePayload = (payload) => {
    setDraftEstimate(payload || null);
    if (!payload) {
      setNoteFactEstimate(null);
      return;
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'note_offers_enabled')) {
      const offersOn = payload.note_offers_enabled !== false;
      setNoteOffersOn(offersOn);
      setNoteFactEstimate(offersOn ? (payload?.note_facts || null) : null);
      return;
    }
    setNoteFactEstimate(payload?.note_facts || null);
  };

  const loadDraftEstimate = (key, usedSceneId, instructionText) => {
    if (!key || !storyId || !usedSceneId) return Promise.resolve(null);
    const ready = estimateReady.current.get(key);
    if (ready) return Promise.resolve(ready);
    const inflight = estimateInflight.current.get(key);
    if (inflight) return inflight;
    const promise = sceneWritingApi.estimate(storyId, {
      sceneId: usedSceneId,
      mergedInstruction: instructionText,
      resolutions,
    }).then((payload) => {
      const next = payload || null;
      if (next) estimateReady.current.set(key, next);
      else estimateInflight.current.delete(key);
      return next;
    }).catch(() => {
      estimateInflight.current.delete(key);
      return null;
    }).finally(() => {
      estimateInflight.current.delete(key);
    });
    estimateInflight.current.set(key, promise);
    return promise;
  };

  const openWriteConfirm = () => {
    if (betaAiPaused()) {
      setAllowancePause(true);
      return;
    }
    const usedSceneId = sceneId || firstSceneId(compose);
    if (!usedSceneId) {
      setActionNote(WRITE_NEED_SCENE);
      return;
    }
    if ((compose?.unresolved_fights || []).length > 0) {
      setActionNote(WRITE_NEED_FIGHT);
      return;
    }
    // #1340 / #1467: notes are optional. An empty box still writes.
    // #1504: hand edits are saved when the writer confirms, after this
    // dialog. Waiting on the save here can finish the estimate and then
    // never reach the write.
    setActionNote('');
    setConfirmNotice('');
    const token = estimateToken.current + 1;
    estimateToken.current = token;
    setWriteConfirm(true);
    const key = draftEstimateSignature({
      storyId,
      sceneId: usedSceneId,
      instruction: instructionRef.current,
      resolutions,
      compose,
    });
    confirmBasisRef.current = key;
    const known = key ? estimateReady.current.get(key) : null;
    if (known) {
      applyEstimatePayload(known);
      setEstimatePending(false);
      return;
    }
    setDraftEstimate(null);
    setEstimatePending(true);
    // The scene record is what the size is for. Asking before it arrives
    // fails, and that failure must not look like a finished size.
    const loadedId = compose?.scene_id ? String(compose.scene_id) : '';
    const sceneLoaded = loadedId && loadedId === String(usedSceneId);
    if (!sceneLoaded || (compose?.unresolved_fights || []).length > 0) return;
    loadDraftEstimate(key, usedSceneId, instructionRef.current)
      .then((payload) => {
        if (estimateToken.current !== token) return;
        applyEstimatePayload(payload);
      })
      .finally(() => {
        if (estimateToken.current !== token) return;
        if (estimateInflight.current.size > 0) return;
        setEstimatePending(false);
      });
  };

  const runGenerate = async (modelId, sceneForThisWrite) => {
    const keptInstruction = instructionRef.current;
    const usedSceneId = sceneForThisWrite || sceneId || firstSceneId(compose);
    const resolvedModel = (modelId || '').trim() || PROSE_SCENE_GENERATION_DEFAULT_MODEL;
    if (!usedSceneId) {
      setActionNote(WRITE_NEED_SCENE);
      setConfirmNotice(WRITE_NEED_SCENE);
      return false;
    }
    setActionNote('');
    setConfirmNotice('');
    setSessionFailure(false);
    setFailureDismissed(false);
    sawWritingRef.current = false;
    const priorKey = passKey(compose);
    setBusy(true);
    setError(null);
    let next = null;
    let refused = false;
    try {
      try {
        const genders = genderChoicesRef.current || undefined;
        genderChoicesRef.current = null;
        next = await sceneWritingApi.generate(storyId, {
          sceneId: usedSceneId,
          mergedInstruction: keptInstruction,
          resolutions,
          modelId: resolvedModel,
          genders,
        });
      } catch (err) {
        // A refused write never started. A dropped connection may still
        // be writing on the server; keep the reload line for that one.
        const status = Number(err?.status);
        refused = status >= 400 && status < 500 && status !== 408 && status !== 499;
        if (refused) {
          clearSceneWrite(storyId);
        }
        setError(lostGenerateMessage(err));
      }
      const text = (next?.generated_text || '').trim();
      if (text) {
        // The draft that was just bought is already the open one. Do not
        // flush the previous draft: that save fails once the new draft is
        // open, and the failure shows beside a draft that arrived.
        applyCompose(next, { keepInstruction: true, recoverPass: true });
        adoptDraft(text);
        clearSceneWrite(storyId);
        sawWritingRef.current = false;
        setSessionFailure(false);
        setError(null);
        markChargeWhenProseVisible();
        notePathChanged();
        return;
      }
      const sameScene = String(sceneIdRef.current || '') === String(usedSceneId);
      const recovered = sameScene ? await recoverThisWrite(priorKey, usedSceneId) : null;
      if (recovered) {
        setError(null);
        sawWritingRef.current = false;
        setSessionFailure(false);
        if (sameScene && !instructionRef.current.trim() && keptInstruction.trim()) {
          setInstruction(keptInstruction);
        }
        markChargeWhenProseVisible();
        notePathChanged();
        return;
      }
      // Only this scene, only this failed attempt. Never a later scene's box.
      if (sameScene && keptInstruction.trim() && instructionRef.current !== keptInstruction) {
        setInstruction(keptInstruction);
      }
      if (next) setError(GENERATE_LOST_COPY);
      if (!refused) {
        sawWritingRef.current = false;
        // A finished response with no draft is this visit's failure.
        // A thrown request may still be writing, so the follow flag stays.
        if (next) clearSceneWrite(storyId);
        setFailureDismissed(false);
        setSessionFailure(true);
      }
    } finally {
      setBusy(false);
    }
  };

  const releaseWrite = () => {
    writeInFlightRef.current = false;
    setConfirming(false);
  };

  const claimWrite = () => {
    if (writeInFlightRef.current) return false;
    writeInFlightRef.current = true;
    setConfirming(true);
    return true;
  };

  const boughtSignature = () => draftEstimateSignature({
    storyId,
    sceneId: sceneIdRef.current || sceneId || firstSceneId(compose),
    instruction: instructionRef.current,
    resolutions,
    compose,
  });

  // The figure on screen is for confirmBasisRef. A pass id or a heartbeat
  // is not part of that figure, so those do not hold the write. Notes,
  // voice, rules, POV, or a different scene do: wait until that draft is
  // priced, then Confirm again. No second write, and no failure line.
  const needsReprice = () => {
    const now = boughtSignature();
    const basis = confirmBasisRef.current;
    if (!basis || !now || now === basis) return false;
    return !estimateReady.current.get(now);
  };

  useEffect(() => {
    if (!styleRoomEnabled() || !writeConfirm) return undefined;
    const usedSceneId = sceneId || firstSceneId(compose);
    const key = draftEstimateSignature({
      storyId,
      sceneId: usedSceneId,
      instruction: instructionRef.current,
      resolutions,
      compose,
    });
    if (!key || key === confirmBasisRef.current) return undefined;
    confirmBasisRef.current = key;
    const token = estimateToken.current + 1;
    estimateToken.current = token;
    setEstimatePending(true);
    loadDraftEstimate(key, usedSceneId, instructionRef.current)
      .then((payload) => {
        if (estimateToken.current !== token) return;
        applyEstimatePayload(payload);
      })
      .finally(() => {
        if (estimateToken.current !== token) return;
        setEstimatePending(false);
      });
    return undefined;
  }, [
    writeConfirm,
    compose?.card?.exemplars,
    compose?.card?.resolved_text,
    compose?.card?.id,
    compose?.card_source,
    sceneId,
    storyId,
    resolutions,
  ]);

  const launchGenerate = (modelId) => {
    if (betaAiPaused()) {
      setWriteConfirm(false);
      setEstimatePending(false);
      setAllowancePause(true);
      return false;
    }
    const usedSceneId = sceneIdRef.current || sceneId || firstSceneId(compose);
    if (!usedSceneId) {
      setActionNote(WRITE_NEED_SCENE);
      setConfirmNotice(WRITE_NEED_SCENE);
      return false;
    }
    const resolvedModel = String(modelId || '').trim() || PROSE_SCENE_GENERATION_DEFAULT_MODEL;
    setActionNote('');
    setConfirmNotice('');
    rememberSceneWrite(storyId, {
      sceneId: usedSceneId,
      priorPassId: passId || compose?.last_pass?.id || compose?.pass_id || '',
    });
    runGenerate(resolvedModel, usedSceneId).finally(releaseWrite);
    return true;
  };

  const startGenerate = (modelId) => {
    if (!claimWrite()) return false;
    const launched = launchGenerate(modelId);
    if (!launched) releaseWrite();
    return launched;
  };

  // Confirm must see the latest flush and the latest scene. The cost
  // dialog stays mounted across the save that set "Saved".
  // 'started' — the write is going. 'unsaved' — the save failed; do not
  // write. 'held' — the write did not start, and its own notice stays up.
  confirmWriteRef.current = async (modelId) => {
    if (writeInFlightRef.current) return 'started';
    if (needsReprice()) return 'repriced';
    if (!claimWrite()) return 'started';
    let launched = false;
    try {
      let kept = false;
      try {
        kept = await flushDraftRef.current({ reask: true });
      } catch {
        kept = false;
      }
      if (kept === 'held') return 'shrink';
      if (kept !== true) return 'unsaved';
      launched = launchGenerate(modelId);
      return launched ? 'started' : 'held';
    } finally {
      if (!launched) releaseWrite();
    }
  };

  // A new draft on an accepted scene is what drops it out of accepted,
  // once the new prose is saved. Ask first. Anything else keeps today's path.
  const beginNewDraft = (next) => {
    if (passStatus === 'accepted') {
      newDraftNextRef.current = next;
      setNewDraftAsk(true);
      return;
    }
    next?.();
  };

  const cancelNewDraftAsk = () => {
    newDraftNextRef.current = null;
    setNewDraftAsk(false);
  };

  const confirmNewDraftAsk = () => {
    const next = newDraftNextRef.current;
    newDraftNextRef.current = null;
    setNewDraftAsk(false);
    next?.();
  };

  const onGenerate = () => {
    beginNewDraft(openWriteConfirm);
  };

  const onRewrite = () => {
    const note = rewriteNote.trim();
    if (note) {
      instructionEdited.current = true;
      instructionRef.current = note;
      setInstruction(note);
      publishChatFocus(note);
    }
    onGenerate();
  };

  const onPointCard = async () => {
    if (!sceneId || !pickCardId) return;
    const next = await run(() => styleCardsApi.setSectionStyle(storyId, sceneId, {
      style_card_id: pickCardId,
    }));
    if (next) {
      setResolutions([]);
      await load(sceneId, { recoverPass: false });
    }
  };

  const onCopyInherited = async () => {
    if (!sceneId) return;
    const card = await run(() => styleCardsApi.copyForScene(storyId, { sceneId }));
    if (card) {
      setResolutions([]);
      await load(sceneId, { recoverPass: false });
    }
  };

  const onPickStyle = async (cardId) => {
    const usedSceneId = sceneId || firstSceneId(compose);
    if (!usedSceneId || !cardId) return;
    const next = await run(() => styleCardsApi.setSectionStyle(storyId, usedSceneId, {
      style_card_id: cardId,
    }));
    if (next) {
      setResolutions([]);
      await load(usedSceneId, { recoverPass: false });
    }
  };

  const onUseBookStyle = async () => {
    const usedSceneId = sceneId || firstSceneId(compose);
    if (!usedSceneId) return;
    const next = await run(() => styleCardsApi.clearSceneStyle(storyId, usedSceneId));
    if (next) {
      setResolutions([]);
      await load(usedSceneId, { recoverPass: false });
    }
  };

  const onSaveLibrary = async () => {
    const cardId = compose?.card?.id;
    if (!cardId) return;
    const card = await run(() => styleCardsApi.saveToLibrary(storyId, cardId));
    if (card) await load(sceneId, { recoverPass: false });
  };

  const onUndoAccept = async () => {
    const usedSceneId = sceneId || firstSceneId(compose);
    const usedPassId = passId || compose?.last_pass?.id || compose?.pass_id;
    if (!usedSceneId) return;
    const dirty = prose !== (passProseRaw(compose) || '');
    const next = await run(() => sceneWritingApi.undoAccept(storyId, {
      sceneId: usedSceneId,
      passId: usedPassId,
    }));
    if (!next) return;
    notePathChanged();
    if (dirty) {
      setCompose((prev) => (prev ? { ...prev, pending_note_offers: [] } : prev));
      return;
    }
    await load(usedSceneId, { recoverPass: false });
  };

  const onAcceptPass = async () => {
    if (acceptingRef.current) return;
    if (noteOffersOn && !acceptCostReady) return;
    const usedSceneId = sceneId || firstSceneId(compose);
    const usedPassId = passId || compose?.last_pass?.id || compose?.pass_id;
    if (!usedSceneId || !usedPassId) return;
    acceptingRef.current = true;
    setAccepting(true);
    try {
      const next = await run(() => sceneWritingApi.accept(storyId, {
        sceneId: usedSceneId,
        passId: usedPassId,
        generatedText: prose,
      }));
      if (!next) {
        setError((current) => current || 'That scene-writing action failed.');
        return;
      }
      applyCompose(next, { recoverPass: true, keepInstruction: true });
      const text = passProseRaw(next);
      if (text) adoptDraft(text);
      notePathChanged();
    } finally {
      acceptingRef.current = false;
      setAccepting(false);
    }
  };

  const onContinueDraft = async () => {
    const usedSceneId = sceneId || firstSceneId(compose);
    const usedPassId = passId || compose?.last_pass?.id || compose?.pass_id;
    let inputTokens = null;
    let draftCheck = null;
    let sizeMissing = true;
    if (usedSceneId) {
      try {
        const sized = await sceneWritingApi.estimateContinue(storyId, {
          sceneId: usedSceneId,
          passId: usedPassId,
          generatedText: prose,
        });
        const tokens = Number(sized?.estimated_input_tokens);
        if (Number.isFinite(tokens) && tokens >= 0) {
          inputTokens = Math.floor(tokens);
          draftCheck = sized?.draft_check || null;
          sizeMissing = false;
        }
      } catch {
        sizeMissing = true;
      }
    }
    const ok = await askPaid('scene_continue', { inputTokens, draftCheck, sizeMissing });
    if (!ok) return;
    if (!usedSceneId || !usedPassId || !prose.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const next = await sceneWritingApi.continueDraft(storyId, {
        sceneId: usedSceneId,
        passId: usedPassId,
        generatedText: prose,
      });
      const text = passProse(next);
      if (!text) {
        setError(GENERATE_LOST_COPY);
        return;
      }
      applyCompose(next, { keepInstruction: true, recoverPass: true });
      adoptDraft(text);
      markChargeWhenProseVisible();
    } catch (err) {
      setError(lostGenerateMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const onCopyDraft = async () => {
    if (!prose) return;
    try {
      if (!navigator.clipboard?.writeText) return;
      await navigator.clipboard.writeText(prose);
    } catch {
      return;
    }
    setDraftCopied(true);
    window.clearTimeout(draftCopyTimer.current);
    draftCopyTimer.current = window.setTimeout(() => setDraftCopied(false), 2000);
  };

  const onResolveOffer = async (offer, action, editedText) => {
    const next = await run(() => sceneWritingApi.resolveNoteOffer(storyId, offer.id, {
      action,
      editedText: editedText || null,
    }));
    if (!next) return;
    applyCompose(next, { recoverPass: true, keepInstruction: true });
    const text = passProseRaw(next);
    if (text) adoptDraft(text);
  };

  const onPromoteBook = async () => {
    const cardId = compose?.card?.id;
    if (!cardId) return;
    const card = await run(() => styleCardsApi.promote(storyId, cardId, { target: 'book' }));
    if (card) await load(sceneId, { recoverPass: false });
  };

  const draftOnScreen = Boolean(
    prose.trim()
    || (compose?.last_pass?.generated_text || '').trim()
    || (compose?.generated_text || '').trim(),
  );
  const visiblePassId = passId || compose?.last_pass?.id || compose?.pass_id || '';
  const canAccept = passStatus !== 'accepted' && Boolean(draftOnScreen && visiblePassId);
  const estimateKey = draftEstimateSignature({
    storyId,
    sceneId,
    instruction: settledInstruction,
    resolutions,
    compose,
  });
  useEffect(() => {
    if (!estimateKey) return undefined;
    const usedSceneId = sceneId || firstSceneId(compose);
    if (!storyId || !usedSceneId) return undefined;
    const known = estimateReady.current.get(estimateKey);
    if (known) {
      applyEstimatePayload(known);
      setEstimatePending(false);
      return undefined;
    }
    // The scene record has the card and the fights. Asking before it
    // arrives, or while a fight is still open, sends an empty resolution
    // list and the server refuses the estimate.
    const loadedId = compose?.scene_id ? String(compose.scene_id) : '';
    if (!loadedId || loadedId !== String(usedSceneId)) return undefined;
    if ((compose?.unresolved_fights || []).length > 0) return undefined;
    let cancelled = false;
    setEstimatePending(true);
    loadDraftEstimate(estimateKey, usedSceneId, settledInstruction)
      .then((payload) => {
        if (cancelled) return;
        applyEstimatePayload(payload);
      })
      .finally(() => {
        if (cancelled) return;
        if (estimateInflight.current.size > 0) return;
        setEstimatePending(false);
      });
    return () => { cancelled = true; };
    // A progress tick keeps the same key, so the cache above does not ask again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateKey, compose]);

  useEffect(() => {
    const rows = compose?.scenes || [];
    const scenesKnown = compose !== null;
    const noOutlineScenes = scenesKnown && rows.length === 0;
    const landedFirstId = (!sceneId && scenesKnown && !noOutlineScenes)
      ? firstSceneId(compose)
      : '';
    const activeId = sceneId || landedFirstId;
    const scene = rows.find((row) => String(row?.id) === String(activeId));
    publishChatSubject(writeRoomChatSubject(scene));
    publishChatSection(activeId || '');
  }, [compose, sceneId]);

  if (pickerOpen) {
    return (
      <StoryPicker
        isOpen
        title="Open a book for scene writing"
        scopedProject={currentProject}
        onClose={() => setPickerOpen(false)}
        onPick={handlePick}
      />
    );
  }

  if (!storyId && !compose) {
    return (
      <div className="sw-page">
        <p className="sw-empty">Select a book to write a scene.</p>
      </div>
    );
  }

  // The picker and "Attach" move to a scene the same way.
  const switchScene = (nextId) => {
    if (!nextId || String(nextId) === String(sceneIdRef.current)) return;
    // An explicit pick wins over a path correction and over an open-next
    // compose that is still in flight.
    openingRef.current = {
      ...openingRef.current,
      pinned: true,
      waitForPath: false,
      correctTo: '',
      sceneId: String(nextId),
      sceneLabel: '',
    };
    if (inflightKey.current === `${storyId}:next`) {
      loadGen.current += 1;
      loadAbortRef.current?.abort();
      inflightKey.current = '';
    }
    resumedRef.current = false;
    setResumeNote('');
    setError(null);
    const from = sceneIdRef.current;
    const go = () => {
      if (String(sceneIdRef.current) !== String(from)) return;
      // #1340 / #1467: each scene has its own notes. Never carry the last
      // scene's notes, or a composed POV line, onto this one.
      instructionEdited.current = false;
      const ownNotes = readStoredInstruction(storyId, nextId);
      instructionRef.current = ownNotes;
      setInstruction(ownNotes);
      setActionNote('');
      setSceneId(nextId);
      const nextSearch = new URLSearchParams(searchParams);
      nextSearch.set('scene', String(nextId));
      setSearchParams(nextSearch, { replace: true });
      setResolutions([]);
      adoptDraft('');
      setOffers([]);
      setPassStatus('draft');
      setPassId('');
      setIncomplete(false);
    };
    if (!draftSave.blocked) {
      go();
      return;
    }
    void (async () => {
      const kept = await draftSave.flush();
      if (kept === 'held') return;
      if (kept !== true && !window.confirm(DRAFT_LEAVE_WARNING)) return;
      go();
    })();
  };
  switchSceneRef.current = switchScene;

  const scenes = compose?.scenes || [];
  const cards = compose?.cards || [];
  const fights = compose?.fights || [];
  const unresolved = compose?.unresolved_fights || [];
  const applying = compose?.applying_rules || [];
  const exceptions = compose?.exception_notes || [];
  const scenesKnown = compose !== null;
  const noOutlineScenes = scenesKnown && scenes.length === 0;
  // Fresh empty book vs still opening: only the list coming back empty
  // is a real Choose. Side Door / Spur Creek Two Step keep the picker
  // aimed at the first outline scene until that list lands.
  const aimingAtFirstScene = Boolean(storyId && !sceneId && !scenesKnown && !error);
  const sceneReady = Boolean(
    compose && (!sceneId || String(compose.scene_id || '') === String(sceneId)),
  );
  const landedFirstId = (!sceneId && scenesKnown && !noOutlineScenes)
    ? firstSceneId(compose)
    : '';
  const activeSceneId = sceneId || landedFirstId;
  // A compose that has arrived is this scene's POV, including the book's
  // mode when no person is chosen. Do not leave the control on Loading
  // after the read finishes.
  const offeredHead = povChipValue(whoseHead, bookPov, compose?.previous_whose_head);
  const shownHead = povPinned ? whoseHead : offeredHead;
  const leadKnown = shownHead?.choice === WHOSE_HEAD_CHOICE_PERSON
    && Boolean((shownHead.entityName || '').trim());
  const povLoading = Boolean(loading && !compose && !leadKnown);
  const sceneListed = scenes.some((scene) => String(scene?.id) === String(activeSceneId));
  // The scene form paints from the scene list as soon as compose has it.
  // A later read that never finishes must not leave "Loading this scene…"
  // on top of that form. sceneReady still waits for this scene's compose
  // id; the line does not.
  const sceneFormReady = Boolean(compose && activeSceneId && sceneListed);
  if (loading && sceneFormReady && boundStoryId === storyId) {
    setLoading(false);
  }
  if (sceneReady && sceneListed) {
    heldScenePaint.current = {
      scenes,
      sceneId: activeSceneId,
      chapterName: chapterBesideScene(
        scenes,
        activeSceneId,
        compose?.chapter_name,
        compose?.chapters,
      ),
      chapters: compose?.chapters || [],
    };
  }
  const held = heldScenePaint.current;
  // Behind the draft dialog, a scene that is not on the list yet would
  // paint "Choose a scene". Keep the scene already shown, or the title
  // that came with the link, until this read lands.
  const keepHeld = Boolean(
    writeConfirm
    && !sceneReady
    && held?.scenes?.length
    && (aimingAtFirstScene || !sceneListed),
  );
  let pickerScenes = keepHeld ? held.scenes : scenes;
  let pickerSceneId = keepHeld ? held.sceneId : activeSceneId;
  const pickerChapters = keepHeld ? (held?.chapters || []) : (compose?.chapters || []);
  const pickerListed = pickerScenes.some((scene) => String(scene?.id) === String(pickerSceneId));
  const handoffTitle = String(handoff.title || '').trim();
  if (writeConfirm && pickerSceneId && !pickerListed && handoffTitle) {
    pickerScenes = [
      ...pickerScenes,
      { id: pickerSceneId, display_name: handoffTitle },
    ];
  }
  const showSceneLoading = (loading || aimingAtFirstScene)
    && !sceneReady
    && !sceneFormReady
    && !writeConfirm;
  const headerScenes = writeConfirm ? pickerScenes : scenes;
  const headerSceneId = writeConfirm ? pickerSceneId : activeSceneId;
  const sceneHeader = (povLoading && !writeConfirm)
    ? ''
    : sceneHeaderLine(
      headerScenes,
      headerSceneId,
      keepHeld ? held.chapterName : compose?.chapter_name,
      keepHeld ? held.chapters : compose?.chapters,
    );
  const scenePickerValue = (aimingAtFirstScene && !writeConfirm)
    ? OPENING_FIRST_SCENE
    : (writeConfirm ? pickerSceneId : activeSceneId);
  const people = peopleFromCompose(compose);
  const povPeople = peopleWithLead(people, shownHead);
  const person = personOverride === null
    ? compose?.person
    : {
      ...(compose?.person || {}),
      scene_mode: personOverride || null,
      mode: personOverride || compose?.person?.mode || null,
      source: personOverride ? 'scene' : compose?.person?.source,
    };
  const voiceName = voiceNameFromCompose(compose);
  const voiceText = voiceTextFromCompose(compose);
  const styleParts = splitStyleSample(voiceText, compose?.card?.exemplars);
  const voiceLine = voiceName || NO_VOICE_YET;
  const noSceneVoice = !voiceName && !voiceText;
  const sceneStyleSet = Boolean(voiceName || compose?.card || compose?.card_source);
  const draftNote = String(instruction || '').trim();
  const povLine = povLineFromCompose(compose);
  const ruleLines = hardRuleLines(compose);
  const writeWhy = busy
    ? null
    : noOutlineScenes
      ? WRITE_NEED_OUTLINE
      : aimingAtFirstScene
        ? null
        : !activeSceneId
          ? WRITE_NEED_SCENE
          : unresolved.length > 0
            ? WRITE_NEED_FIGHT
            : null;
  const acceptWhy = busy
    ? null
    : passStatus === 'accepted'
      ? ACCEPT_ALREADY
      : canAccept
        ? null
        : (aimingAtFirstScene || loading || draftOnScreen || generationKind !== 'idle')
          ? null
          : ACCEPT_NEED_DRAFT;
  const shownWriteWhy = generationKind === 'writing' ? null : (writeWhy || actionNote || null);
  const writeDisabled = Boolean(writeWhy)
    || busy
    || aimingAtFirstScene
    || (loading && !activeSceneId)
    || generationKind === 'writing';
  const acceptNeedsCost = canAccept && noteOffersOn && !acceptCostReady;
  const acceptDisabled = Boolean(acceptWhy) || busy || !canAccept || acceptNeedsCost;
  const showContinue = passStatus !== 'accepted' && incomplete && Boolean(prose.trim()) && !acceptWhy;
  // Run 8: after Accept the picker hid behind "Show scene context". The path
  // names the next undrafted scene in story order, here and beside Accept.
  const pathTip = bookPathTip(bookPath, {
    stage: 'scene-writing',
    storyId,
    currentSceneId: activeSceneId,
    currentAccepted: passStatus === 'accepted',
  });
  // Above the draft, three info lines used to stack: saved-to-story,
  // the drafted/accepted counts, and this step. Keep the step
  // (After this, or the path's own next-scene wording). The outline
  // sentence stays only when the path has no step yet.
  const outlineTip = journeyNextTip({
    stage: 'scene-writing',
    storyId,
    sceneCount: scenes.length,
    scenesKnown,
  });
  const afterThisTip = pathTip?.action
    ? { ...pathTip, text: '' }
    : (pathTip ? null : outlineTip);
  const onPathStep = (tip) => {
    if (tip?.sceneId) switchScene(tip.sceneId);
  };
  const dismissDraftFailure = () => {
    sawWritingRef.current = false;
    setSessionFailure(false);
    setFailureDismissed(true);
    clearSceneWrite(storyId);
  };
  const retryFailedDraft = async () => {
    if (betaAiPaused()) {
      setAllowancePause(true);
      return;
    }
    const go = async () => {
      if (draftSave.blocked) {
        const kept = await draftSave.flush();
        if (kept !== true) return;
      }
      startGenerate();
    };
    beginNewDraft(() => { void go(); });
  };
  // One sentence, one place. A pass already on screen is marked in
  // This draft. An empty scene says the first draft is still being
  // written, beside Write draft. Scene context staying open must not
  // paint a second copy next to the button.
  const replacingPass = generationKind === 'writing' && fullSurface;
  const followBesideWrite = generationKind !== 'idle' && !fullSurface;
  const followOnWriting = generationKind !== 'idle' && fullSurface && !replacingPass;
  const followLine = (here, { testId = false, retry = false } = {}) => (here ? (
    <>
      <p
        className="sw-empty sw-write-follow"
        role="status"
        data-testid={testId ? 'scene-write-status' : undefined}
      >
        {generationView.message}
      </p>
      {retry && generationKind === 'stopped' && (
        <>
          <button type="button" className="sw-btn" onClick={retryFailedDraft}>
            {TRY_AGAIN_LABEL}
          </button>
          <button type="button" className="sw-btn" onClick={dismissDraftFailure}>
            {UNDO_DISMISS}
          </button>
        </>
      )}
    </>
  ) : null);
  const openReview = unresolved.length > 0;

  // Unknown project → let the free GET decide (it refuses non-series books).
  const seriesKnown = currentProject && currentProject.id === activeProjectId
    ? Boolean(currentProject.is_series)
    : undefined;
  // Run 11: an empty POV character picker in a series book points at
  // Bring into this book. Renders nothing for books with no series people.
  const bringSeriesPeople = people.length === 0 && storyId ? (
    <BringSeriesPeopleOffer storyId={storyId} isSeries={seriesKnown} />
  ) : null;

  const retryOpen = () => {
    void (async () => {
      if (draftSave.blocked) {
        const kept = await draftSave.flush();
        if (kept === 'held') return;
        if (kept !== true && !window.confirm(DRAFT_LEAVE_WARNING)) return;
      }
      load(sceneId || undefined, { force: true });
    })();
  };

  const openError = error && !isRawSceneError(error) ? (
    <div className="sw-error" role="alert">
      <p>{error}</p>
      <button type="button" className="sw-btn" onClick={retryOpen}>
        {TRY_AGAIN_LABEL}
      </button>
    </div>
  ) : null;

  // Run 52: once the page shows prose, "This scene" folds away behind "Show scene
  // context", and the scene list went with it. The list now sits under the header.
  const scenePickerField = (
    <div className="sw-field">
      <label htmlFor="sw-scene">Scene</label>
      <select
        id="sw-scene"
        value={scenePickerValue}
        disabled={aimingAtFirstScene && !writeConfirm}
        aria-busy={showSceneLoading}
        onChange={(e) => {
          const nextId = e.target.value;
          if (nextId === OPENING_FIRST_SCENE) return;
          switchScene(nextId);
        }}
      >
        {(aimingAtFirstScene && !writeConfirm) ? (
          <option value={OPENING_FIRST_SCENE}>
            {openingSlow ? STILL_OPENING_FIRST : OPENING_FIRST_SCENE_LABEL}
          </option>
        ) : (
          <option value="">Choose a scene</option>
        )}
        {scenePickerGroups(pickerScenes, pickerChapters).map((group) => (
          group.name ? (
            <optgroup
              key={group.key}
              label={group.name}
              {...(group.chapter !== undefined ? {
                'data-chapter': group.chapter,
                'data-sequence': group.sequence || '',
              } : {})}
            >
              {group.scenes.map((scene) => (
                <option key={scene.id} value={scene.id}>
                  {sceneOptionLabel(scene, handoff.title)}
                </option>
              ))}
            </optgroup>
          ) : group.scenes.map((scene) => (
            <option key={scene.id} value={scene.id}>
              {sceneOptionLabel(scene, handoff.title)}
            </option>
          ))
        ))}
      </select>
    </div>
  );

  const pageHeader = (
      <header className="sw-header">
        <h1>Scene writing</h1>
        {sceneHeader && <p className="sw-header__scene" data-testid="sw-header-scene">{sceneHeader}</p>}
        <BookToolsGroup />
        {fullSurface && <div className="sw-header__picker">{scenePickerField}</div>}
        <JourneyNextTip tip={afterThisTip} onAction={onPathStep} />
      </header>
  );

  const sceneSection = (
      <section className="sw-panel" aria-labelledby="sw-scene-heading">
        <h2 id="sw-scene-heading">This scene</h2>
        {!fullSurface && scenePickerField}
        <div className="sw-actions sw-actions--write" data-testid="sw-write-actions">
          <button
            type="button"
            className={`sw-btn${fullSurface ? '' : ' sw-btn--primary'}`}
            disabled={writeDisabled}
            onClick={onGenerate}
          >
            {WRITE_DRAFT}
          </button>
          <WriteMoreMenu
            onNewDraft={onGenerate}
            onNotes={() => {
              if (!contextOpen) toggleContext();
              window.setTimeout(() => document.getElementById('sw-instruction')?.focus(), 0);
            }}
            onCheck={() => document.getElementById('sw-check-before')?.focus()}
            onContext={toggleContext}
            newDraftDisabled={writeDisabled}
          />
          {shownWriteWhy && <span className="sw-why">{shownWriteWhy}</span>}
          {followLine(followBesideWrite, { testId: !fullSurface, retry: !fullSurface })}
          {openingSlow && (loading || aimingAtFirstScene) && (
            <button type="button" className="sw-btn" onClick={retryOpen}>
              {TRY_AGAIN_LABEL}
            </button>
          )}
        </div>
        <div className="sw-heads">
          <WhoseHeadControl
            id="sw-whose-head"
            value={shownHead}
            people={povPeople}
            castIds={whoseCastIds}
            disabled={busy || !activeSceneId}
            onChange={onSetWhoseHead}
            emptyAction={bringSeriesPeople}
            onAddPerson={storyId ? onAddPovPerson : null}
            loading={povLoading}
          />
          <PreviousPovOffer
            previous={povLoading ? null : compose?.previous_whose_head}
            current={shownHead}
            disabled={busy || !activeSceneId}
            onUse={onSetWhoseHead}
          />
          <PersonControl
            id="sw-person"
            person={person}
            disabled={busy || !activeSceneId}
            hideNoBookDefault={noSceneVoice}
            onChange={onSetPerson}
          />
          {personNote ? <p className="sw-hint" role="status">{personNote}</p> : null}
        </div>
        <div className="sw-field">
          <span id="sw-voice-label">{VOICE_LABEL}</span>
          {styleRoomEnabled() && voiceName ? (
            <p className="sw-style-name" aria-labelledby="sw-voice-label">
              <span className="sw-style-name__text">{voiceName}</span>
              {styleSourceLabel(compose?.card_source) ? (
                <span className="sw-style-tag">{styleSourceLabel(compose.card_source)}</span>
              ) : null}
            </p>
          ) : (
            <p className="sw-readonly" aria-labelledby="sw-voice-label">{voiceLine}</p>
          )}
          {styleRoomEnabled() ? (
            <>
              {styleParts.description ? (
                <p className="sw-card-text">{styleParts.description}</p>
              ) : null}
              {styleParts.sample ? (
                <div className="sw-style-sample">
                  <p className="sw-style-sample__label">{SAMPLE_OF_THE_SOUND}</p>
                  <p className="sw-style-sample__text">{styleParts.sample}</p>
                </div>
              ) : null}
              {!voiceText && !styleParts.sample
                ? (sceneStyleSet ? null : <p className="sw-empty">{NO_VOICE_ON_SCENE}</p>)
                : null}
            </>
          ) : (
            voiceText ? (
              <p className="sw-card-text">{voiceText}</p>
            ) : (noSceneVoice ? null : <p className="sw-empty">{NO_VOICE_ON_SCENE}</p>)
          )}
          {styleRoomEnabled() ? (
            <StyleChoice
              name={voiceName}
              source={compose?.card_source}
              styles={cards}
              showLine={!voiceName}
              busy={busy}
              onPick={onPickStyle}
              onUseBook={onUseBookStyle}
              onNew={() => navigate(`${styleCardsPath(storyId)}?new=1`)}
            />
          ) : (
            <>
              <div className="sw-row">
                <div className="sw-field">
                  <label htmlFor="sw-pick-card">{PICK_ANOTHER_VOICE}</label>
                  <select
                    id="sw-pick-card"
                    value={pickCardId}
                    onChange={(e) => setPickCardId(e.target.value)}
                  >
                    <option value="">{CHOOSE_A_VOICE}</option>
                    {cards.map((card) => (
                      <option key={card.id} value={card.id}>{card.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="sw-actions">
                <button type="button" className="sw-btn" disabled={busy || !pickCardId} onClick={onPointCard}>
                  {USE_THIS_VOICE}
                </button>
              </div>
            </>
          )}
        </div>
        {noOutlineScenes && (
          <>
            <p className="sw-empty">{SCENE_WRITE_NO_SCENES}</p>
            <PlotTrackAddScene
              columns={[]}
              cells={[]}
              onAdd={async ({ afterSceneId, whatHappens, title }) => {
                const saved = await plottrackApi.addScene(storyId, {
                  afterSceneId, whatHappens, title,
                });
                if (saved?.scene_id) await load(saved.scene_id, { force: true });
                return saved;
              }}
              onSuggestTitle={(what) => plottrackApi.suggestSceneTitle(storyId, what)}
            />
          </>
        )}
        {/* Migration 183: drafts whose scene was deleted stay here, never lost. */}
        <DetachedDrafts
          storyId={storyId}
          scenes={scenes}
          onAttached={(attachedId) => (
            String(attachedId) === String(activeSceneId)
              ? load(attachedId, { recoverPass: true })
              : switchScene(attachedId)
          )}
        />
        <div className="sw-field sw-field--wide">
          <label htmlFor="sw-instruction">{INSTRUCTIONS_OPTIONAL}</label>
          <textarea
            id="sw-instruction"
            value={instruction}
            onChange={(e) => {
              instructionEdited.current = true;
              setInstruction(e.target.value);
            }}
            placeholder={DRAFT_NOTES_PLACEHOLDER}
          />
          {(voiceText || draftNote || povLine || ruleLines.length > 0) && (
            <div className="sw-will-send" data-testid="sw-will-send">
              <p className="sw-source">{SCENE_CONTEXT_HEADING}</p>
              <WillSendLines
                voiceName={voiceName}
                voiceText={voiceText}
                draftNote={draftNote}
                povLine={povLine}
                hardRules={ruleLines}
              />
            </div>
          )}
        </div>
        {/* #1341: no people in Notes → offer the cast before drafting. */}
        <CastOffer
          storyId={storyId}
          projectId={activeProjectId}
          onAdded={() => load(sceneIdRef.current, { recoverPass: false })}
        />
      </section>
  );

  const confirmBlock = (
    <>
      {paidDialog}
      {allowancePause && (
        <PaidActionConfirm paused onCancel={() => setAllowancePause(false)} />
      )}
      {draftSave.shrinkAsk != null && (
        <ShrinkGuardDialog
          onSave={() => {
            void draftSave.confirmShrink().then((saved) => {
              if (saved === true && leaveAfterShrinkRef.current) {
                leaveAfterShrinkRef.current = false;
                setEditingAccepted(false);
              }
            });
          }}
          onKeepEditing={() => {
            leaveAfterShrinkRef.current = false;
            draftSave.dismissShrink();
            proseEditRef.current?.focus();
          }}
        />
      )}
      {restoreAsk && (
        <RestoreAcceptedDialog
          onPutBack={() => { void confirmRestoreAccepted(); }}
          onKeepEdits={() => setRestoreAsk(false)}
        />
      )}
      {newDraftAsk && (
        <NewDraftOnAcceptedDialog
          onCancel={cancelNewDraftAsk}
          onGoAhead={confirmNewDraftAsk}
        />
      )}
      {writeConfirm && (
        <WriteDraftConfirm
          estimatedInputTokens={draftEstimate?.estimated_input_tokens}
          promptChars={draftEstimate?.prompt_chars}
          outputBudget={draftEstimate?.worst_output_tokens}
          typicalOutput={draftEstimate?.typical_output_tokens}
          draftCheck={draftEstimate?.draft_check}
          sizePending={estimatePending}
          confirming={confirming}
          notice={confirmNotice}
          replacesEditedDraft={replacesEditedDraft}
          handEditedAccepted={acceptedHandEdited}
          genderNeeded={compose?.gender_needed || []}
          styleChoice={styleRoomEnabled() ? (
            <StyleChoice
              name={voiceName}
              source={compose?.card_source}
              styles={cards}
              busy={busy}
              onPick={onPickStyle}
              onUseBook={onUseBookStyle}
              onNew={() => navigate(`${styleCardsPath(storyId)}?new=1`)}
            />
          ) : null}
          onConfirm={(modelId, genders) => {
            genderChoicesRef.current = genders || null;
            void confirmWriteRef.current(modelId).then((result) => {
              if (result !== 'started') genderChoicesRef.current = null;
              if (result === 'held' || result === 'repriced') return;
              // A failed save leaves its message on the draft. Close the
              // cost dialog so that message is visible. A started write
              // closes it too. A missing scene keeps the dialog and shows
              // its own notice.
              estimateToken.current += 1;
              setWriteConfirm(false);
              setEstimatePending(false);
            });
          }}
          onCancel={() => {
            genderChoicesRef.current = null;
            estimateToken.current += 1;
            setWriteConfirm(false);
            setEstimatePending(false);
          }}
        />
      )}
    </>
  );

  const reviewBlock = (
      <details className="sw-review" open={openReview}>
        <summary>{REVIEW_CONTEXT}</summary>
        <p className="sw-hint">{SCENE_WRITE_PLOTTRACK_HINT}</p>
        {compose?.banner && <p className="sw-banner">{compose.banner}</p>}
        <p className="sw-source">
          {styleRoomEnabled()
            ? (styleSourceLabel(compose?.card_source) || (voiceName ? voiceName : NO_STYLE_SET))
            : (compose?.card_source || compose?.card
              ? `From the book: ${voiceName || 'Book default'}`
              : NO_VOICE_YET)}
          {compose?.pov?.source ? ` (from ${sourceLabel(compose.pov.source)})` : ''}
        </p>
        <div className="sw-actions">
          {!styleRoomEnabled() && (
            <button type="button" className="sw-btn" disabled={busy} onClick={onCopyInherited}>
              {COPY_BOOK_VOICE_INTO_SCENE}
            </button>
          )}
          <button type="button" className="sw-btn" disabled={busy || !compose?.card?.id} onClick={onSaveLibrary}>
            Save to library
          </button>
          <button type="button" className="sw-btn" disabled={busy || !compose?.card?.id} onClick={onPromoteBook}>
            {MAKE_THIS_THE_BOOK_VOICE}
          </button>
        </div>
        <p className="sw-hint">{SCENE_VOICE_REPLACES}</p>
        <SceneContextSummary
          summary={compose?.context_summary}
          voiceName={voiceName}
          voiceText={voiceText}
          draftNote={draftNote}
          povLine={povLine}
          hardRules={ruleLines}
        />
        <section className="sw-panel" aria-labelledby="sw-rules-heading">
          <h2 id="sw-rules-heading">Story Rules</h2>
          <p className="sw-hint">{RULE_SITS_NEXT_TO_VOICE}</p>
          {applying.length === 0 ? (
            <p className="sw-empty">No Story Rules apply to this scene.</p>
          ) : (
            <ul className="sw-list">
              {applying.map((rule) => (
                <li
                  key={rule.id}
                  className={`sw-rule${rule.enabled ? '' : ' sw-rule--off'}`}
                >
                  <div className="sw-rule__meta">
                    <span className="sw-rule__state">{rule.enabled ? 'On' : 'Off'}</span>
                    <span>{rule.scope_label}</span>
                  </div>
                  <p className="sw-card-text">{rule.sentence}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
        {fights.length > 0 && (
          <section className="sw-panel" aria-labelledby="sw-fight-heading">
            <h2 id="sw-fight-heading">A fight — no silent winner</h2>
            <ul className="sw-list">
              {fights.map((fight) => (
                <li key={fight.id} className="sw-fight">
                  <div className="sw-fight__meta">
                    <span className="sw-fight__kind">
                      {fight.fence_kind === 'fact' ? 'Rule about a fact' : RULE_ABOUT_VOICE}
                    </span>
                    <span>{fight.rule_scope_label}</span>
                  </div>
                  <p className="sw-card-text">{fight.reason}</p>
                  <p className="sw-source">Rule: {fight.rule_sentence}</p>
                  {fight.resolution ? (
                    <p className="sw-banner">{fight.resolution.note}</p>
                  ) : (
                    <div className="sw-actions">
                      <button
                        type="button"
                        className="sw-btn"
                        disabled={busy}
                        onClick={() => onResolve(fight, 'keep_rule')}
                      >
                        Keep the rule
                      </button>
                      <button
                        type="button"
                        className="sw-btn"
                        disabled={busy}
                        onClick={() => onResolve(fight, 'keep_card')}
                      >
                        {KEEP_THE_VOICE}
                      </button>
                      <button
                        type="button"
                        className="sw-btn"
                        disabled={busy}
                        onClick={() => onResolve(fight, 'edit')}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="sw-btn"
                        disabled={busy}
                        onClick={() => onResolve(fight, 'skip_exception')}
                      >
                        Skip — this scene is an exception on this draft
                      </button>
                    </div>
                  )}
                  {fight.fence_kind === 'fact' && (
                    <p className="sw-hint">A rule about a fact still holds.</p>
                  )}
                  {fight.fence_kind === 'voice' && (
                    <p className="sw-hint">{VOICE_RULE_YIELDS}</p>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
        {exceptions.length > 0 && (
          <section className="sw-panel sw-exception" aria-labelledby="sw-exception-heading">
            <h2 id="sw-exception-heading">Exception on this draft</h2>
            {exceptions.map((note) => (
              <p key={note} className="sw-banner">{note}</p>
            ))}
          </section>
        )}
      </details>
  );

  const passSection = fullSurface ? (
        <div className="sw-panel sw-pass">
          <h2 id="sw-prose-heading">{SCENE_DRAFT_LABEL}</h2>
          <p
            className="sw-banner"
            role={replacingPass ? 'status' : undefined}
            data-testid={replacingPass ? 'scene-write-status' : undefined}
          >
            {replacingPass
              ? NEW_PASS_ON_THE_WAY
              : passStatus === 'accepted'
                ? PASS_ACCEPTED
                : PASS_IS_DRAFT}
          </p>
          {passStatus !== 'accepted' && incomplete && (
            <p className="sw-banner">{PASS_STOPPED_EARLY}</p>
          )}
          {passStatus !== 'accepted' && !replacingPass && (
            <div id="sw-check-before" tabIndex={-1}>
              <CheckBeforeAccepting checks={compose?.last_pass?.checks} />
            </div>
          )}
          <div className="sw-actions sw-writing__bar">
            {followLine(followOnWriting, { testId: true, retry: true })}
            {passStatus !== 'accepted' && (
              <button
                type="button"
                className="sw-btn"
                onClick={() => document.getElementById('sw-prose-edit')?.focus()}
              >
                {EDIT_ACCEPTED_SCENE}
              </button>
            )}
            <WriteMoreMenu
              onNewDraft={onGenerate}
              onNotes={() => {
                if (!contextOpen) toggleContext();
                window.setTimeout(() => document.getElementById('sw-instruction')?.focus(), 0);
              }}
              onCheck={() => document.getElementById('sw-check-before')?.focus()}
              onContext={toggleContext}
              newDraftDisabled={writeDisabled}
            />
            {passStatus !== 'accepted' && (
            <button
              type="button"
              className="sw-btn sw-btn--primary"
              disabled={acceptDisabled || accepting}
              aria-busy={accepting ? 'true' : undefined}
              onClick={onAcceptPass}
            >
              {accepting ? DRAFT_SAVING : ACCEPT_DRAFT}
            </button>
            )}
            {passStatus !== 'accepted' && (
              <div className="sw-rewrite">
                <label htmlFor="sw-rewrite-note">{REWRITE_NOTE_LABEL}</label>
                <input
                  id="sw-rewrite-note"
                  type="text"
                  value={rewriteNote}
                  placeholder={REWRITE_NOTE_PLACEHOLDER}
                  onChange={(event) => setRewriteNote(event.target.value)}
                />
                <button
                  type="button"
                  className="sw-btn"
                  disabled={writeDisabled}
                  onClick={onRewrite}
                >
                  {REWRITE_DRAFT}
                </button>
              </div>
            )}
            {canAccept && noteOffersOn && (
              <AcceptNoteCheckCost noteFacts={noteFactEstimate} onReady={onAcceptCost} />
            )}
            {acceptWhy && <span className="sw-why">{acceptWhy}</span>}
            {passStatus === 'accepted' && !editingAccepted && (
              <button
                type="button"
                className="sw-btn"
                data-testid="sw-edit-accepted"
                aria-label="Edit this scene"
                disabled={busy}
                onClick={() => setEditingAccepted(true)}
              >
                {EDIT_ACCEPTED_SCENE}
              </button>
            )}
            {passStatus === 'accepted' && !editingAccepted && (
              <button
                type="button"
                className="sw-btn"
                data-testid="sw-undo-accept"
                disabled={busy}
                onClick={() => { void onUndoAccept(); }}
              >
                {UNDO_ACCEPT}
              </button>
            )}
            {passStatus === 'accepted' && editingAccepted && (
              <button
                type="button"
                className="sw-btn"
                data-testid="sw-stop-editing"
                disabled={busy}
                onClick={() => { void onStopEditing(); }}
              >
                {STOP_EDITING}
              </button>
            )}
            {passStatus === 'accepted' && compose?.last_pass?.has_accepted_original === true && (
              <button
                type="button"
                className="sw-btn"
                data-testid="sw-restore-accepted"
                disabled={busy}
                onClick={onRestoreAccepted}
              >
                {RESTORE_ACCEPTED_VERSION}
              </button>
            )}
            {passStatus === 'accepted' && pathTip?.sceneId && (
              <button
                type="button"
                className="sw-btn sw-btn--primary"
                data-testid="sw-next-scene"
                disabled={busy}
                onClick={() => onPathStep(pathTip)}
              >
                {pathTip.action}
              </button>
            )}
            {showContinue && (
              <>
                <button
                  type="button"
                  className="sw-btn"
                  disabled={busy || !prose.trim()}
                  onClick={onContinueDraft}
                >
                  {CONTINUE_THIS_DRAFT}
                </button>
                <span className="sw-why">{CONTINUE_VS_NEW_DRAFT}</span>
              </>
            )}
          </div>
          {passStatus === 'accepted' && compose?.last_pass?.edited_since_notes === true && (
            <p className="sw-banner" data-testid="sw-edited-since-notes">{EDITED_SINCE_NOTES}</p>
          )}
          {showProseEditor ? (
            <div className="sw-field sw-field--wide sw-prose-field">
              <div className="sw-prose-head">
                <label htmlFor="sw-prose-edit">{REVISE_THIS_PASS}</label>
                <button
                  type="button"
                  className="sw-btn sw-copy"
                  disabled={!prose}
                  aria-live="polite"
                  onClick={() => { void onCopyDraft(); }}
                >
                  {draftCopied ? COPIED_SCENE_DRAFT : COPY_SCENE_DRAFT}
                </button>
              </div>
              <textarea
                id="sw-prose-edit"
                ref={proseEditRef}
                className="sw-prose-edit sw-prose-edit--full"
                value={prose}
                disabled={busy}
                aria-label={SCENE_DRAFT_LABEL}
                aria-describedby={
                  draftSave.status === 'idle' ? 'sw-prose-hint' : 'sw-prose-hint sw-draft-save'
                }
                onChange={(e) => setProse(e.target.value)}
                onBlur={() => { void draftSave.flush(); }}
              />
              <p id="sw-prose-hint" className="sw-hint">
                {passStatus === 'accepted' ? ACCEPTED_SCENE_EDIT_HINT : REVISE_THIS_PASS_HINT}
              </p>
              {styleRoomEnabled() && writtenWithLine(compose?.last_pass, modelNames) && (
                <p className="sw-hint sw-written-with" data-testid="sw-written-with">
                  {writtenWithLine(compose?.last_pass, modelNames)}
                </p>
              )}
              {showBringBack ? (
                <div className="sw-actions">
                  <button type="button" className="sw-btn" onClick={onBringBackEdited}>
                    {BRING_BACK_EDITED_DRAFT}
                  </button>
                </div>
              ) : null}
              {draftSave.status !== 'idle' && (
                <p
                  id="sw-draft-save"
                  className="sw-hint sw-draft-save"
                  role={draftSave.status === 'error' ? 'alert' : 'status'}
                  data-testid="sw-draft-save"
                >
                  {draftSave.status === 'saving' && DRAFT_SAVING}
                  {draftSave.status === 'saved' && DRAFT_SAVED}
                  {draftSave.status === 'error' && (
                    <>
                      <span>{DRAFT_SAVE_FAILED}</span>
                      <button
                        type="button"
                        className="sw-btn"
                        onClick={() => { void draftSave.flush(); }}
                      >
                        {DRAFT_SAVE_RETRY}
                      </button>
                    </>
                  )}
                </p>
              )}
            </div>
          ) : (
            <div className="sw-prose-read">
              <div className="sw-prose-head">
                <button
                  type="button"
                  className="sw-btn sw-copy"
                  disabled={!prose}
                  aria-live="polite"
                  onClick={() => { void onCopyDraft(); }}
                >
                  {draftCopied ? COPIED_SCENE_DRAFT : COPY_SCENE_DRAFT}
                </button>
              </div>
              <p className="sw-prose sw-prose--full">{prose}</p>
            </div>
          )}
        </div>
  ) : null;

  const offerBlock = noteOffersOn && offers.length > 0 ? (
        <NoteOfferList
          offers={offers}
          lede={compose?.note_offers_lede || NOTE_OFFERS_LEDE}
          busy={busy}
          editingOfferId={editingOfferId}
          editDraft={editDraft}
          onEditStart={(offer) => {
            setEditingOfferId(offer.id);
            setEditDraft(offer.proposed_text || '');
          }}
          onEditChange={setEditDraft}
          onEditCancel={() => {
            setEditingOfferId('');
            setEditDraft('');
          }}
          onAccept={(offer) => onResolveOffer(offer, 'accept')}
          onSkip={(offer) => onResolveOffer(offer, 'skip')}
          onSaveEdit={(offer) => onResolveOffer(offer, 'edit', editDraft)}
        />
  ) : null;

  if (fullSurface) {
    const phoneWriteButton = isPhone && !prose.trim() ? (
      <button
        type="button"
        className="sw-btn sw-btn--primary sw-phone-write"
        disabled={writeDisabled}
        onClick={onGenerate}
      >
        {WRITE_DRAFT}
      </button>
    ) : null;
    const writingSurface = (
      <div
        className="sw-writing"
        data-testid="sw-writing-surface"
        role="region"
        aria-labelledby="sw-prose-heading"
      >
        {phoneWriteButton}
        {pageHeader}
        {openError}
        {resumeNote && <p className="sw-resume-note" role="status">{resumeNote}</p>}
        {showSceneLoading && (
          <p className="sw-empty" role="status">
            {openingSlow ? STILL_OPENING_SCENE : 'Loading this scene…'}
          </p>
        )}
        {passSection}
        {offerBlock ? (noteHost ? createPortal(offerBlock, noteHost) : offerBlock) : null}
      </div>
    );
    if (isPhone) {
      return (
        <div className="sw-page sw-page--full">
          <div className="sw-full">
            {writingSurface}
            <section className="sw-scene-settings" aria-label={SCENE_SETTINGS}>
              <button
                type="button"
                className="sw-context__toggle sw-scene-settings__toggle"
                aria-expanded={phoneSettingsOpen}
                aria-controls="sw-context-panel"
                onClick={() => setPhoneSettingsOpen((open) => !open)}
              >
                {SCENE_SETTINGS}
              </button>
              <div
                id="sw-context-panel"
                className="sw-context__body"
                hidden={!phoneSettingsOpen}
              >
                {sceneSection}
                {reviewBlock}
              </div>
            </section>
          </div>
          {confirmBlock}
        </div>
      );
    }
    return (
      <div className="sw-page sw-page--full">
        <div className="sw-full">
          <aside
            className={`sw-context${contextOpen ? ' sw-context--open' : ''}`}
            aria-label="Scene context"
          >
            <button
              type="button"
              className="sw-context__toggle"
              aria-expanded={contextOpen}
              aria-controls="sw-context-panel"
              onClick={toggleContext}
            >
              {contextOpen ? HIDE_SCENE_CONTEXT : SHOW_SCENE_CONTEXT}
            </button>
            <div
              id="sw-context-panel"
              className="sw-context__body"
              hidden={!contextOpen}
            >
              {sceneSection}
              {reviewBlock}
            </div>
          </aside>
          {writingSurface}
        </div>
        {confirmBlock}
      </div>
    );
  }

  return (
    <div className="sw-page">
      {pageHeader}
      {openError}
      {resumeNote && <p className="sw-resume-note" role="status">{resumeNote}</p>}
      {showSceneLoading && (
        <p className="sw-empty" role="status">
          {openingSlow ? STILL_OPENING_SCENE : 'Loading this scene…'}
        </p>
      )}
      {sceneSection}
      {confirmBlock}
      {reviewBlock}
    </div>
  );
}

function CheckBeforeAccepting({ checks }) {
  const parsed = parseDraftCheck(checks);
  const nothing = parsed.status === 'ran' && parsed.items.length === 0;
  return (
    <section className="sw-checks" aria-labelledby="sw-checks-heading" data-testid="sw-checks">
      <h3 id="sw-checks-heading">{CHECK_BEFORE_ACCEPTING}</h3>
      <p className="sw-checks__caution" data-testid="sw-check-caution">{CHECK_READ_IT_YOURSELF}</p>
      <p className="sw-hint">{CHECK_BEFORE_ACCEPTING_HINT}</p>
      {nothing ? (
        <p data-testid="sw-check-none">{CHECK_NO_DISAGREEMENTS}</p>
      ) : null}
      {parsed.status === 'could_not_run' ? (
        <p role="status" data-testid="sw-check-failed">{CHECK_COULD_NOT_RUN}</p>
      ) : null}
      {parsed.items.length > 0 ? (
        <ul>
          {parsed.items.map((item) => {
            const generic = isGenericCheckSource(item.source);
            const sourceLabel = generic
              ? ''
              : item.source_kind === 'scene'
                ? `Scene ${String(item.source).replace(/^scene\s+/i, '')}`
                : item.source;
            return (
            <li key={`${item.source}-${item.draft}-${item.other}`} className="sw-checks__pair">
              {sourceLabel ? <p className="sw-source">{sourceLabel}</p> : null}
              <p>
                <span className="sw-offer__label">{CHECK_THIS_DRAFT}: </span>
                {item.draft}
              </p>
              <p>
                <span className="sw-offer__label">
                  {item.source_kind === 'scene' ? CHECK_THAT_SCENE : CHECK_THAT_NOTE}
                  {': '}
                </span>
                {item.other}
              </p>
            </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}

function NoteOfferList({
  offers,
  lede,
  busy,
  editingOfferId,
  editDraft,
  onEditStart,
  onEditChange,
  onEditCancel,
  onAccept,
  onSkip,
  onSaveEdit,
}) {
  return (
    <section className="sw-panel" aria-labelledby="sw-offers-heading">
      <h2 id="sw-offers-heading">{PROPOSED_NOTE_UPDATES}</h2>
      <p className="sw-hint">{lede}</p>
      <p className="sw-hint">{STANDING_IDENTITY_STAYS}</p>
      <ul className="sw-list">
        {offers.map((offer) => {
          const editing = editingOfferId === offer.id;
          const stampCurrent = offer.offer_kind === 'stamp_current'
            || (offer.proposed_text || '') === (offer.current_text || '');
          const replacing = offer.offer_kind === 'replace_line';
          return (
            <li key={offer.id} className="sw-offer">
              <p className="sw-source">{offer.note_name || 'Note'}</p>
              <p className="sw-hint">{ONE_NOTE_CHANGE}</p>
              {editing ? (
                <div className="sw-field sw-field--wide">
                  <label htmlFor={`sw-offer-edit-${offer.id}`}>
                    {stampCurrent ? CURRENT_STATE_AFTER_SCENE : EDIT_THIS_FACT}
                  </label>
                  <textarea
                    id={`sw-offer-edit-${offer.id}`}
                    value={editDraft}
                    onChange={(e) => onEditChange(e.target.value)}
                  />
                </div>
              ) : replacing ? (
                <>
                  <p className="sw-card-text">
                    <span className="sw-offer__label">{THIS_LINE_CHANGES}: </span>
                    {offer.replaces_line || offer.current_text}
                  </p>
                  <p className="sw-card-text">
                    <span className="sw-offer__label">{LINE_BECOMES}: </span>
                    {offer.proposed_text}
                  </p>
                </>
              ) : stampCurrent ? (
                <p className="sw-card-text">
                  <span className="sw-offer__label">{CURRENT_STATE_AFTER_SCENE}: </span>
                  {offer.proposed_text}
                </p>
              ) : (
                <>
                  {offer.current_text ? (
                    <p className="sw-card-text">
                      <span className="sw-offer__label">{STAYS_ON_THE_NOTE}: </span>
                      {offer.current_text}
                    </p>
                  ) : null}
                  <p className="sw-card-text">
                    <span className="sw-offer__label">{ADD_TO_THE_NOTE}: </span>
                    {offer.proposed_text}
                  </p>
                </>
              )}
              <div className="sw-actions">
                {editing ? (
                  <>
                    <button
                      type="button"
                      className="sw-btn"
                      disabled={busy || !editDraft.trim()}
                      onClick={() => onSaveEdit(offer)}
                    >
                      {SAVE_EDITED_NOTE_UPDATE}
                    </button>
                    <button
                      type="button"
                      className="sw-btn"
                      disabled={busy}
                      onClick={onEditCancel}
                    >
                      {CANCEL_EDIT_NOTE_UPDATE}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="sw-btn"
                      disabled={busy}
                      onClick={() => onAccept(offer)}
                    >
                      {ACCEPT_NOTE_UPDATE}
                    </button>
                    <button
                      type="button"
                      className="sw-btn"
                      disabled={busy}
                      onClick={() => onEditStart(offer)}
                    >
                      {EDIT_NOTE_UPDATE}
                    </button>
                    <button
                      type="button"
                      className="sw-btn"
                      disabled={busy}
                      onClick={() => onSkip(offer)}
                    >
                      {SKIP_NOTE_UPDATE}
                    </button>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function WillSendLines({ voiceName = '', voiceText = '', draftNote = '', povLine = '', hardRules = [] }) {
  const note = String(draftNote || '').trim();
  return (
    <>
      {voiceText ? (
        <>
          {voiceName ? <p className="sw-source">{voiceName}</p> : null}
          <p className="sw-card-text">{voiceText}</p>
        </>
      ) : null}
      {note ? (
        <>
          <p className="sw-source">{NOTES_FOR_THIS_DRAFT}</p>
          <p className="sw-card-text">{note}</p>
        </>
      ) : null}
      {povLine ? <p className="sw-card-text">{povLine}</p> : null}
      {hardRules.map((line) => (
        <p key={line} className="sw-card-text">{`Honor this hard rule: ${line}`}</p>
      ))}
    </>
  );
}

function SceneContextSummary({
  summary,
  voiceName = '',
  voiceText = '',
  draftNote = '',
  povLine = '',
  hardRules = [],
}) {
  const note = String(draftNote || '').trim();
  if (!summary && !voiceText && !note && !povLine && hardRules.length === 0) return null;
  const threads = summary?.plottrack_threads || [];
  const standing = summary?.standing_notes || [];
  const mutable = summary?.mutable_notes || [];
  const ambiguities = summary?.ambiguities || [];
  const conflicts = summary?.conflicts || [];
  const walls = summary?.knowledge_boundaries || {};
  const prior = summary?.prior_prose || {};
  const secrets = walls.must_stay_secret || [];
  const objective = walls.objective || [];
  const recent = prior.recent || [];
  const emptyNotes = Boolean(summary?.book_notes_empty);
  const hasAnything = threads.length > 0 || standing.length > 0 || mutable.length > 0
    || ambiguities.length > 0 || conflicts.length > 0
    || secrets.length > 0 || objective.length > 0 || recent.length > 0
    || Boolean(prior.is_first_in_sequence)
    || Boolean(voiceText)
    || Boolean(note)
    || Boolean(povLine)
    || hardRules.length > 0;
  if (!hasAnything) return null;

  return (
    <section className="sw-panel" aria-labelledby="sw-context-heading">
      <h2 id="sw-context-heading">{SCENE_CONTEXT_HEADING}</h2>
      <p className="sw-hint">{SCENE_CONTEXT_LEDE}</p>
      {summary?.notes_win_line ? (
        <p className="sw-hint" data-testid="sw-notes-win">{summary.notes_win_line}</p>
      ) : null}
      <WillSendLines
        voiceName={voiceName}
        voiceText={voiceText}
        draftNote={note}
        povLine={povLine}
        hardRules={hardRules}
      />
      {threads.length > 0 && (
        <div className="sw-context-block">
          <h3>{PLOT_TRACK}</h3>
          <p className="sw-hint">{summary?.row_order_line || ROW_ORDER_NOT_BEAT_ORDER}</p>
          <ul className="sw-list">
            {threads.map((thread) => (
              <li key={`${thread.label}-${thread.state}`} className="sw-context-thread">
                <p className="sw-source">
                  {thread.label || 'Through-line'}
                  {thread.state ? ` · ${thread.state}` : ''}
                  {thread.visibility ? ` · ${thread.visibility}` : ''}
                </p>
                {thread.note ? <p className="sw-card-text">{thread.note}</p> : null}
                {(thread.beats || []).length > 0 && (
                  <ul className="sw-beats">
                    {thread.beats.map((beat) => (
                      <li key={beat}>{beat}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {standing.length > 0 && (
        <div className="sw-context-block">
          <h3>Book Notes</h3>
          <ul className="sw-list">
            {standing.map((note) => (
              <li key={note.name || note.text}>
                <p className="sw-source">{note.name}</p>
                <p className="sw-card-text">{note.text}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
      {mutable.length > 0 && (
        <div className="sw-context-block">
          {summary?.mutable_chip ? (
            <p className="sw-chip" role="status">{summary.mutable_chip || MUTABLE_NOTE_CHIP}</p>
          ) : (
            <p className="sw-hint">{AS_OF_ENTRY_HINT}</p>
          )}
          <ul className="sw-list">
            {mutable.map((note) => (
              <li key={note.note_id || note.name || note.text}>
                <p className="sw-source">
                  {note.name}
                  {note.resolution === 'as_of_entry' ? ` · ${AS_OF_ENTRY_HINT}` : ''}
                </p>
                <p className="sw-card-text">{note.text}</p>
                {note.long_writeup ? (
                  <p className="sw-card-text">{note.long_writeup}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}
      {emptyNotes && standing.length === 0 && mutable.length === 0 && (
        <p className="sw-empty">No Book Notes yet. Write still runs from Plot Grid plus the voice and hard rules.</p>
      )}
      <div className="sw-context-block">
        {objective.length > 0 && (
          <>
            <h3>{KNOWLEDGE_WALLS_HEADING}</h3>
            <ul className="sw-list">
              {objective.map((text) => (
                <li key={text}><p className="sw-card-text">{text}</p></li>
              ))}
            </ul>
          </>
        )}
        <p className="sw-source">{MUST_STAY_SECRET_HEADING}</p>
        <p className="sw-hint">{MUST_STAY_SECRET_HINT}</p>
        {secrets.length > 0 ? (
          <ul className="sw-list">
            {secrets.map((text) => (
              <li key={text}><p className="sw-card-text">{text}</p></li>
            ))}
          </ul>
        ) : (
          <p className="sw-empty">{MUST_STAY_SECRET_EMPTY}</p>
        )}
      </div>
      {(recent.length > 0 || prior.is_first_in_sequence) && (
        <div className="sw-context-block">
          <h3>{PRIOR_PROSE_HEADING}</h3>
          {prior.is_first_in_sequence ? (
            <p className="sw-hint">{SEQUENCE_BOUNDARY_LINE}</p>
          ) : null}
          <ul className="sw-list">
            {recent.map((item) => (
              <li key={item.scene_name || item.excerpt}>
                <p className="sw-source">{item.scene_name || 'Earlier scene'}</p>
                <p className="sw-card-text">{item.excerpt}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(summary?.entry_exit?.entry_label || summary?.entry_exit?.exit_label) && (
        <div className="sw-context-block">
          {summary?.entry_exit?.entry_label ? (
            <p className="sw-source">{summary.entry_exit.entry_label || ENTRY_HEADING}</p>
          ) : null}
          {summary?.entry_exit?.exit_label ? (
            <p className="sw-source">{summary.entry_exit.exit_label || EXIT_HEADING}</p>
          ) : null}
        </div>
      )}
      {ambiguities.map((item) => (
        <p key={item.code + (item.detail || '')} className="sw-banner">{item.detail}</p>
      ))}
      {conflicts.map((item) => (
        <p key={item.code + (item.detail || '')} className="sw-banner">{item.detail}</p>
      ))}
    </section>
  );
}
